<template>
  <div>
    <dx-grid
      :url="url"
      :columns="columns"
      :actions="actions"
      :title="title"
      :beforeSetupGrid="beforeSetupGrid"
      :onRowClicked="onRowClicked"
      navigationPath="onRowClicked"
      navigationField="onRowClicked"
      ref="grid"
    ></dx-grid>

    <!-- Add New Bus Change Dialog -->
    <v-dialog persistent v-model="addDialog" max-width="1200">
      <v-card class="bus-change-dialog">
        <v-card-title class="dialog-header">
          <v-icon class="mr-3" color="white" size="28">mdi-bus-clock</v-icon>
          <span class="dialog-title">{{
            isEditing ? "Edit Bus Change" : "New Bus Change"
          }}</span>
          <v-spacer></v-spacer>
          <v-btn class="white--text" icon dark @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="px-4">
          <!-- Account Search Section (only for new and when no account selected) -->
          <div v-if="!isEditing && !selectedAccount">
            <div class="section-header mb-2">
              <v-icon small class="mr-1">mdi-account-search</v-icon>
              Search Account
            </div>

            <!-- Phone Number Search -->
            <v-layout row wrap class="mb-1">
              <v-flex xs12 sm6 class="pr-2">
                <v-text-field
                  v-model="phoneSearch"
                  label="Search by Phone Number"
                  prepend-inner-icon="mdi-phone"
                  outlined
                  dense
                  clearable
                  mask="###-###-####"
                  @input="searchByPhone"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <!-- Search Name Mode Toggle -->
            <v-layout row wrap align-center class="mb-2">
              <v-flex xs12 sm6 class="pr-2">
                <div class="caption grey--text mb-1">Or Search By Name</div>
                <v-btn-toggle
                  v-model="searchNameMode"
                  mandatory
                  class="search-toggle"
                >
                  <v-btn flat small value="yiddish">אידיש</v-btn>
                  <v-btn flat small value="english">English</v-btn>
                </v-btn-toggle>
              </v-flex>
            </v-layout>

            <!-- Account Search Fields -->
            <v-layout row wrap class="mb-0">
              <v-flex xs12 sm6 class="pr-2">
                <v-text-field
                  v-model="lastNameSearch"
                  :label="
                    searchNameMode === 'yiddish' ? 'לעצטע נאמען' : 'Last Name'
                  "
                  prepend-inner-icon="mdi-account"
                  outlined
                  dense
                  clearable
                  @input="searchByName"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6>
                <v-text-field
                  v-model="firstNameSearch"
                  :label="
                    searchNameMode === 'yiddish' ? 'טאטעס נאמען' : 'Father Name'
                  "
                  prepend-inner-icon="mdi-account"
                  outlined
                  dense
                  clearable
                  @input="searchByName"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <!-- Address Search -->
            <v-layout row wrap class="mb-1 mt-2">
              <v-flex xs12>
                <div class="caption grey--text mb-1">Or Search By Address</div>
              </v-flex>
            </v-layout>
            <v-layout row wrap class="mb-0">
              <v-flex xs12 sm6 class="pr-2">
                <v-text-field
                  v-model="houseNumberSearch"
                  label="House Number"
                  prepend-inner-icon="mdi-home"
                  outlined
                  dense
                  clearable
                  @input="searchByAddress"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6>
                <v-text-field
                  v-model="streetSearch"
                  label="Street Name"
                  prepend-inner-icon="mdi-road"
                  outlined
                  dense
                  clearable
                  @input="searchByAddress"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <!-- Account List -->
            <div class="account-list-container mb-3" v-if="accounts.length > 0">
              <div class="caption grey--text my-2 px-2">
                Select an account ({{ accounts.length }} found)
              </div>
              <v-list dense class="account-list">
                <v-list-tile
                  v-for="account in accounts"
                  :key="account.accountID"
                  @click="selectAccount(account)"
                  class="account-item"
                >
                  <v-list-tile-avatar>
                    <v-icon color="rgb(92, 184, 96)">mdi-account-box</v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title>
                      <strong>{{ account.name }}</strong>
                      <span class="grey--text ml-2">({{ account.hname }})</span>
                    </v-list-tile-title>
                    <v-list-tile-sub-title>
                      Account ID: {{ account.accountID }}
                      <span v-if="account.homePhone" class="ml-1">
                        | {{ account.homePhone }}
                      </span>
                      <span v-if="account.cellPhone" class="ml-1">
                        | {{ account.cellPhone }}
                      </span>
                      <span v-if="account.address1" class="ml-1">
                        | {{ account.address1 }}
                      </span>
                      <span v-if="account.busAddress1" class="ml-1">
                        | Bus: {{ account.busAddress1 }}
                      </span>
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                  <v-list-tile-action>
                    <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
                  </v-list-tile-action>
                </v-list-tile>
              </v-list>
            </div>

            <!-- No Accounts Message -->
            <v-alert
              v-if="showNoAccountsMessage && accounts.length === 0"
              type="warning"
              outline
              dense
              value="true"
              class="mb-3"
            >
              No accounts found. Please try a different search.
            </v-alert>
          </div>

          <!-- Selected Account and Student Selection (only for new) -->
          <div v-if="!isEditing && selectedAccount && !selectedStudent">
            <!-- Selected Account Card -->
            <v-card class="selected-account-card mb-4" outlined>
              <v-card-text class="pa-0">
                <v-layout align-center>
                  <v-avatar color="rgb(92, 184, 96)" size="40" class="mr-2">
                    <v-icon dark size="24">mdi-account-box</v-icon>
                  </v-avatar>
                  <v-flex>
                    <div class="body-2 font-weight-medium">
                      {{ selectedAccount.name }}
                      <span class="caption grey--text ml-1"
                        >({{ selectedAccount.hname }})</span
                      >
                    </div>
                    <div class="caption grey--text">
                      <strong>Account ID:</strong>
                      {{ selectedAccount.accountID }}
                      <span v-if="selectedAccount.address1" class="ml-2">
                        | {{ selectedAccount.address1 }}
                      </span>
                    </div>
                  </v-flex>
                  <v-btn icon small @click="clearAccount">
                    <v-icon color="grey">mdi-close-circle</v-icon>
                  </v-btn>
                </v-layout>
              </v-card-text>
            </v-card>

            <!-- Student List from Selected Account -->
            <div class="section-header mb-3">
              <v-icon small class="mr-1">mdi-account-multiple</v-icon>
              Select Student
            </div>

            <div v-if="loadingStudents" class="text-center pa-4">
              <v-progress-circular
                indeterminate
                color="rgb(92, 184, 96)"
              ></v-progress-circular>
              <div class="mt-2 grey--text">Loading students...</div>
            </div>

            <v-alert
              v-else-if="students.length === 0"
              type="info"
              outline
              dense
              value="true"
              class="mb-3"
            >
              <span v-if="userBranch">
                No students found for this account in branch{{
                  userBranch.includes(",") ? "es" : ""
                }}
                {{ userBranch }}.
              </span>
              <span v-else>
                No students found for this account.
              </span>
            </v-alert>

            <div v-else class="student-list-container">
              <v-list dense two-line class="student-list">
                <v-list-tile
                  v-for="student in students"
                  :key="student.studentID"
                  @click="selectStudent(student)"
                  class="student-item"
                >
                  <v-list-tile-avatar>
                    <v-icon color="rgb(92, 184, 96)">mdi-account-circle</v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title>
                      <strong>{{ student.hebName }}</strong>
                      <span class="grey--text ml-2"
                        >({{ student.engName }})</span
                      >
                    </v-list-tile-title>
                    <v-list-tile-sub-title>
                      Grade: {{ student.class }} | Building:
                      {{ student.building }}
                      <span v-if="student.branch" class="ml-2">
                        | Branch: {{ student.branch }}
                      </span>
                      <span
                        v-if="student.teacherH || student.teacherH2"
                        class="ml-2"
                      >
                        | Teacher:
                        {{
                          [student.teacherH, student.teacherH2]
                            .filter((t) => t)
                            .join(" / ")
                        }}
                      </span>
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                  <v-list-tile-action>
                    <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
                  </v-list-tile-action>
                </v-list-tile>
              </v-list>
            </div>
          </div>

          <!-- Selected Student Card (shows when student is selected) -->
          <div v-if="selectedStudent">
            <v-card class="selected-student-card mb-4" outlined>
              <v-card-text class="pa-0">
                <v-layout align-center>
                  <v-avatar color="rgb(92, 184, 96)" size="40" class="mr-2">
                    <v-icon dark size="24">mdi-account</v-icon>
                  </v-avatar>
                  <v-flex>
                    <v-layout row wrap>
                      <v-flex xs12 sm6 class="pr-2">
                        <div class="body-2 font-weight-medium pb-0">
                          {{ selectedStudent.studentName }}
                          <span class="caption grey--text ml-1"
                            >({{ selectedStudent.hebName }})</span
                          >
                        </div>
                        <div class="caption grey--text">
                          <strong>Student ID:</strong>
                          {{ selectedStudent.studentID }} |
                          <strong>Building:</strong>
                          {{ selectedStudent.building }} |
                          <strong>Class:</strong> {{ selectedStudent.class }}-{{
                            selectedStudent.section
                          }}
                        </div>
                      </v-flex>
                      <v-flex xs12 sm6>
                        <div
                          class="caption grey--text"
                          v-if="
                            selectedStudent.teacherH ||
                              selectedStudent.teacherH2
                          "
                        >
                          <v-icon x-small class="mr-1">mdi-account-tie</v-icon>
                          {{ getTeacherDisplay(selectedStudent) }}
                        </div>
                        <div
                          class="caption grey--text"
                          v-if="selectedStudent.address1"
                        >
                          <v-icon x-small class="mr-1">mdi-home</v-icon>
                          {{ selectedStudent.address1 }}
                        </div>
                      </v-flex>
                    </v-layout>
                  </v-flex>
                  <v-btn v-if="!isEditing" icon small @click="clearStudent">
                    <v-icon color="grey">mdi-close-circle</v-icon>
                  </v-btn>
                </v-layout>
              </v-card-text>
            </v-card>
          </div>

          <!-- Record Info with Recording (Edit Mode Only) -->
          <div
            v-if="isEditing && editingRecordInfo"
            class="mb-3 record-info-with-audio"
          >
            <div class="record-info-compact">
              <span class="info-item">
                <v-icon x-small class="mr-1">mdi-source-branch</v-icon>
                {{ editingRecordInfo.source || "-" }}
              </span>
              <span class="info-divider">•</span>
              <span class="info-item">
                <v-icon x-small class="mr-1">mdi-account-edit</v-icon>
                {{ editingRecordInfo.enteredBy || "-" }}
              </span>
              <span class="info-divider">•</span>
              <span class="info-item">
                <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                {{ formatDateTime(editingRecordInfo.enteredOn) }}
              </span>
            </div>

            <!-- Recording Section (if recording exists) -->
            <div v-if="editingRecordInfo.recordingURL" class="recording-inline">
              <div class="recording-header">
                <v-icon small color="#ff6b6b" class="recording-icon"
                  >mdi-microphone</v-icon
                >
                <span class="recording-label">Original Request</span>
              </div>
              <audio
                ref="audioPlayer"
                controls
                controlsList="nodownload"
                :src="editingRecordInfo.recordingURL"
                class="recording-player"
              ></audio>
            </div>
          </div>

          <v-layout v-if="selectedStudent" row wrap>
            <v-flex xs12 md3 class="pr-2">
              <div class="section-header mb-3">
                <v-icon small class="mr-1">mdi-account-details</v-icon>
                Info
              </div>
              <v-card outlined class="pa-2 compact-info">
                <div class="info-row">
                  <span class="info-label">Student:</span>
                  <span class="info-value">{{
                    selectedStudent.studentName || "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">ID:</span>
                  <span class="info-value">{{
                    selectedStudent.studentID || "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Source:</span>
                  <span class="info-value">{{
                    editingRecordInfo && editingRecordInfo.source
                      ? editingRecordInfo.source
                      : "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Entered:</span>
                  <span class="info-value">{{
                    editingRecordInfo && editingRecordInfo.enteredOn
                      ? formatDateTime(editingRecordInfo.enteredOn)
                      : "-"
                  }}</span>
                </div>
              </v-card>
            </v-flex>

            <!-- Bus Change Details Section -->
            <v-flex xs12 md6 class="px-2">
            <div class="section-header mb-3">
              <v-icon small class="mr-1">mdi-bus-marker</v-icon>
              Bus Change Details
            </div>

            <v-layout row wrap>
              <v-flex xs12 sm6 class="pr-2">
                <v-card outlined class="pa-2 period-section-card mb-2">
                  <div class="caption period-title mb-1">Starting Run</div>
                  <v-menu
                    v-model="startDateMenu"
                    :close-on-content-click="false"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="newBusChange.startDate"
                        label="Start Date"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        hide-details
                        class="mb-2"
                        v-on="on"
                        :rules="[rules.required]"
                        ref="startDate"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="newBusChange.startDate"
                      color="rgb(92, 184, 96)"
                      @input="onStartDateChange"
                    ></v-date-picker>
                  </v-menu>
                  <v-select
                    v-model="newBusChange.startPeriod"
                    :items="periodOptions"
                    item-text="label"
                    item-value="value"
                    label="Run *"
                    prepend-inner-icon="mdi-weather-sunset-up"
                    outlined
                    dense
                    hide-details
                  ></v-select>
                </v-card>
              </v-flex>
              <v-flex xs12 sm6 class="pl-2">
                <v-card outlined class="pa-2 period-section-card mb-2">
                  <div class="caption period-title mb-1">Ending After</div>
                  <v-menu
                    v-model="endDateMenu"
                    :close-on-content-click="false"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="newBusChange.endDate"
                        label="End Date"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        hide-details
                        class="mb-2"
                        v-on="on"
                        :rules="[rules.required]"
                        ref="endDate"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="newBusChange.endDate"
                      color="rgb(92, 184, 96)"
                      :min="newBusChange.startDate"
                      @input="endDateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                  <v-select
                    v-model="newBusChange.endPeriod"
                    :items="periodOptions"
                    item-text="label"
                    item-value="value"
                    label="Run *"
                    prepend-inner-icon="mdi-weather-sunset-down"
                    outlined
                    dense
                    hide-details
                  ></v-select>
                </v-card>
              </v-flex>
            </v-layout>
            <div
              class="caption grey--text text--darken-1 mb-3 period-helper-text"
            >
              Morning Pickup: Includes the trip TO school. Afternoon Dropoff:
              Includes the trip FROM school.
            </div>

            <v-alert
              v-if="deadlineWarningMessage"
              type="warning"
              outline
              dense
              value="true"
              class="mb-3 pa-2"
            >
              {{ deadlineWarningMessage }}
            </v-alert>

            <v-layout row wrap>
              <v-flex xs12 sm6 class="pr-2">
                <v-text-field
                  v-model="newBusChange.busChangeAddress"
                  label="Bus Change Address"
                  prepend-inner-icon="mdi-map-marker"
                  outlined
                  dense
                  :rules="[rules.required]"
                  ref="busChangeAddress"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 class="pl-2">
                <v-text-field
                  v-model="newBusChange.busChangePhoneNumber"
                  label="Bus Change Phone Number"
                  prepend-inner-icon="mdi-phone"
                  outlined
                  dense
                  :rules="[rules.phoneNumber]"
                  ref="busChangePhoneNumber"
                  mask="###-###-####"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs12 sm6 class="pr-2">
                <v-autocomplete
                  v-model="selectedPickupRoute"
                  :items="filteredPickupRoutes"
                  :filter="customRouteFilter"
                  item-text="description"
                  item-value="id"
                  label="Pickup Route"
                  prepend-inner-icon="mdi-bus-clock"
                  outlined
                  dense
                  clearable
                  :loading="loadingBusRoutes"
                  placeholder="Start typing to search..."
                  return-object
                  @change="onPickupRouteChange"
                  class="route-autocomplete"
                  :menu-props="{ bottom: true, offsetY: true }"
                >
                  <template v-slot:selection="{ item }">
                    <div class="route-selection-wrapper">
                      <div class="route-selection-main">
                        {{ item.description }}
                        <span
                          v-if="item.busNumber"
                          class="route-detail-chip"
                          :style="getRouteChipStyle(item)"
                        >
                          Bus #{{ item.busNumber }}
                        </span>
                      </div>
                      <div v-if="item.driver" class="route-selection-driver">
                        <v-icon x-small>mdi-account</v-icon> {{ item.driver }}
                      </div>
                    </div>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-tile-content
                      class="route-list-item"
                      :style="
                        getRouteColor(item.color)
                          ? `border-left: 4px solid ${getRouteColor(
                              item.color
                            )}`
                          : ''
                      "
                    >
                      <v-list-tile-title>
                        <strong>{{ item.description }}</strong>
                      </v-list-tile-title>
                      <v-list-tile-sub-title>
                        <span v-if="item.busNumber" class="mr-2">
                          <v-icon x-small>mdi-bus</v-icon> Bus #{{
                            item.busNumber
                          }}
                        </span>
                        <span v-if="item.driver" class="mr-2">
                          <v-icon x-small>mdi-account</v-icon> {{ item.driver }}
                        </span>
                        <span v-if="item.color" class="mr-2">
                          <v-icon
                            x-small
                            :style="
                              getRouteColor(item.color)
                                ? `color: ${getRouteColor(item.color)}`
                                : ''
                            "
                            >mdi-circle</v-icon
                          >
                          {{ item.color }}
                        </span>
                      </v-list-tile-sub-title>
                    </v-list-tile-content>
                  </template>
                </v-autocomplete>
              </v-flex>
              <v-flex xs12 sm6 class="pl-2">
                <v-autocomplete
                  v-model="selectedDropoffRoute"
                  :items="filteredDropoffRoutes"
                  :filter="customRouteFilter"
                  item-text="description"
                  item-value="id"
                  label="Dropoff Route"
                  prepend-inner-icon="mdi-bus-marker"
                  outlined
                  dense
                  clearable
                  :loading="loadingBusRoutes"
                  placeholder="Start typing to search..."
                  return-object
                  class="route-autocomplete"
                  :menu-props="{ bottom: true, offsetY: true }"
                >
                  <template v-slot:selection="{ item }">
                    <div class="route-selection-wrapper">
                      <div class="route-selection-main">
                        {{ item.description }}
                        <span
                          v-if="item.busNumber"
                          class="route-detail-chip"
                          :style="getRouteChipStyle(item)"
                        >
                          Bus #{{ item.busNumber }}
                        </span>
                      </div>
                      <div v-if="item.driver" class="route-selection-driver">
                        <v-icon x-small>mdi-account</v-icon> {{ item.driver }}
                      </div>
                    </div>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-tile-content
                      class="route-list-item"
                      :style="
                        getRouteColor(item.color)
                          ? `border-left: 4px solid ${getRouteColor(
                              item.color
                            )}`
                          : ''
                      "
                    >
                      <v-list-tile-title>
                        <strong>{{ item.description }}</strong>
                      </v-list-tile-title>
                      <v-list-tile-sub-title>
                        <span v-if="item.busNumber" class="mr-2">
                          <v-icon x-small>mdi-bus</v-icon> Bus #{{
                            item.busNumber
                          }}
                        </span>
                        <span v-if="item.driver" class="mr-2">
                          <v-icon x-small>mdi-account</v-icon> {{ item.driver }}
                        </span>
                        <span v-if="item.color" class="mr-2">
                          <v-icon
                            x-small
                            :style="
                              getRouteColor(item.color)
                                ? `color: ${getRouteColor(item.color)}`
                                : ''
                            "
                            >mdi-circle</v-icon
                          >
                          {{ item.color }}
                        </span>
                      </v-list-tile-sub-title>
                    </v-list-tile-content>
                  </template>
                </v-autocomplete>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs12 sm6 class="pr-2">
                <v-text-field
                  v-model="newBusChange.busChangeName"
                  label="Bus Change Family Name"
                  prepend-inner-icon="mdi-account"
                  outlined
                  dense
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 class="pl-2">
                <v-text-field
                  v-model="newBusChange.busChangeAccountID"
                  label="Bus Change Account ID"
                  prepend-inner-icon="mdi-account-search"
                  outlined
                  dense
                  readonly
                  @click="openAccountSearch"
                  style="cursor: pointer;"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs12 sm6 class="pr-2">
                <v-text-field
                  v-model="newBusChange.notificationPhoneNumber"
                  label="Notification Phone"
                  prepend-inner-icon="mdi-bell"
                  outlined
                  dense
                  :rules="[rules.phoneNumber]"
                  mask="###-###-####"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 class="pl-2">
                <v-select
                  v-model="newBusChange.notificationType"
                  :items="['MESSAGE', 'CALL']"
                  label="Notification Type"
                  prepend-inner-icon="mdi-message-alert"
                  outlined
                  dense
                  clearable
                ></v-select>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs12 sm6 class="pr-2">
                <v-text-field
                  v-model="newBusChange.geoPhoneNumber"
                  label="Geo Tracking Phone Number"
                  prepend-inner-icon="mdi-phone-in-talk"
                  outlined
                  dense
                  :rules="[rules.phoneNumber]"
                  mask="###-###-####"
                  autocomplete="no-password"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 class="pl-2">
                <v-textarea
                  v-model="newBusChange.note"
                  label="Note"
                  prepend-inner-icon="mdi-note-text"
                  outlined
                  dense
                  rows="2"
                  autocomplete="no-password"
                  hide-details
                ></v-textarea>
              </v-flex>
            </v-layout>
            </v-flex>

            <v-flex xs12 md3 class="pl-2">
              <div class="section-header mb-3">
                <v-icon small class="mr-1">mdi-message-text</v-icon>
                Messages
              </div>
              <v-card outlined class="pa-2 comments-card">
                <div
                  v-if="!isEditing"
                  class="grey--text text--darken-1"
                >
                  Messages are available after save.
                </div>
                <div
                  v-else-if="loadingBusChangeComments"
                  class="d-flex align-center grey--text text--darken-1"
                >
                  <v-progress-circular
                    size="18"
                    width="2"
                    indeterminate
                    color="success"
                    class="mr-2"
                  ></v-progress-circular>
                  Loading...
                </div>
                <div
                  v-else-if="busChangeComments.length === 0"
                  class="grey--text text--darken-1"
                >
                  No messages.
                </div>
                <div v-else>
                  <div
                    v-for="(comment, index) in busChangeComments"
                    :key="comment.id || comment.ID || comment.callUUID || index"
                    class="comment-entry"
                  >
                    <div class="comment-meta">
                      <span class="font-weight-medium">
                        {{ getCommentEnteredBy(comment) || "Parent" }}
                      </span>
                      <span class="mx-1">-</span>
                      <span>{{
                        formatDateTime(getCommentEnteredOn(comment))
                      }}</span>
                    </div>
                    <div
                      v-if="getCommentCallerID(comment)"
                      class="comment-caller grey--text text--darken-1"
                    >
                      <v-icon x-small class="mr-1">mdi-phone</v-icon>
                      {{ formatPhoneNumber(getCommentCallerID(comment)) }}
                    </div>
                    <div
                      v-if="getCommentText(comment)"
                      class="comment-text grey--text text--darken-3"
                    >
                      {{ getCommentText(comment) }}
                    </div>
                    <audio
                      v-if="getCommentRecordingUrl(comment)"
                      controls
                      controlsList="nodownload"
                      :src="getCommentRecordingUrl(comment)"
                      class="comment-audio mt-2"
                    ></audio>
                  </div>
                </div>
              </v-card>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-divider class="py-0"></v-divider>

        <v-card-actions class="py-3 pr-3">
          <v-btn v-if="isEditing" color="error" dark @click="openDeleteDialog">
            <v-icon left>mdi-delete</v-icon>
            Delete
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" dark @click="closeDialog">
            Cancel
          </v-btn>
          <v-btn
            color="success"
            :dark="!!selectedStudent"
            :loading="saving"
            :disabled="!selectedStudent"
            @click="saveBusChange"
            class="save-btn"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save Bus Change
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="450">
      <v-card>
        <v-card-title class="headline error white--text">
          <v-icon left dark>mdi-alert-circle</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outline value="true" class="mb-0 pa-2">
            <strong>Warning:</strong> This action cannot be undone. The bus
            change record will be permanently deleted.
          </v-alert>
          <div class="mt-2" v-if="recordToDelete">
            <div>
              <strong>Student:</strong>
              {{ recordToDelete.studentName }}
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" dark :loading="deleting" @click="confirmDelete">
            <v-icon left>mdi-delete</v-icon>
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Grades Setup Dialog -->
    <v-dialog v-model="gradesSetupDialog" max-width="1000" persistent>
      <v-card>
        <v-card-title class="dialog-header">
          <v-icon class="mr-3" color="white" size="28">mdi-cog</v-icon>
          <span class="dialog-title">Bus Change Settings by Grade</span>
          <v-spacer></v-spacer>
          <v-btn class="white--text" icon dark @click="closeGradesSetupDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Grades List View -->
        <v-card-text class="pa-3" v-if="!editingGrade">
          <v-layout row wrap class="mb-3" align-center>
            <v-flex xs12 sm4 class="pr-2">
              <v-text-field
                v-model="gradesSetupSearch"
                placeholder="Search grades..."
                prepend-inner-icon="mdi-magnify"
                clearable
                solo
                flat
                dense
                hide-details
                background-color="#f5f5f5"
                class="compact-search"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm3 class="pr-2" v-if="isAdmin()">
              <v-autocomplete
                v-model="gradesSetupBranchFilter"
                :items="branchList"
                placeholder="All Branches"
                solo
                flat
                dense
                hide-details
                clearable
                background-color="#f5f5f5"
              ></v-autocomplete>
            </v-flex>
            <v-spacer></v-spacer>
            <v-btn
              v-if="selectedGrades.length > 0"
              small
              color="success"
              outline
              @click="openBulkExceptionDialog"
              class="mr-2"
            >
              <v-icon small left>mdi-calendar-plus</v-icon>
              Add Exception ({{ selectedGrades.length }})
            </v-btn>
            <v-btn small color="success" dark @click="openAddGrade">
              <v-icon small left>mdi-plus</v-icon>
              Add Grade
            </v-btn>
          </v-layout>

          <div class="grades-list" v-if="!loadingGradesSetup">
            <div
              v-for="grade in filteredBusChangeGradesSearched"
              :key="grade.id"
              class="grade-row"
              @click="openEditGrade(grade)"
            >
              <div class="grade-select" @click.stop>
                <v-checkbox
                  v-model="selectedGrades"
                  :value="grade.id"
                  color="success"
                  hide-details
                  class="mt-0 pt-0"
                ></v-checkbox>
              </div>
              <div class="grade-info">
                <span class="grade-name">{{ grade.grade }}</span>
                <span class="grade-branch">{{ grade.branch }}</span>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon
                      x-small
                      :color="grade.allowBusChange ? '#4caf50' : '#9e9e9e'"
                      class="ml-2"
                      v-on="on"
                    >
                      {{
                        grade.allowBusChange
                          ? "mdi-check-circle"
                          : "mdi-close-circle"
                      }}
                    </v-icon>
                  </template>
                  <span>{{
                    grade.allowBusChange
                      ? "Bus change allowed"
                      : "Bus change not allowed"
                  }}</span>
                </v-tooltip>
              </div>
              <div class="grade-actions" @click.stop>
                <v-btn class="my-0" icon x-small @click="openEditGrade(grade)">
                  <v-icon x-small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  class="my-0"
                  icon
                  x-small
                  @click="confirmDeleteGrade(grade)"
                >
                  <v-icon x-small color="error">mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
            <div
              v-if="filteredBusChangeGradesSearched.length === 0"
              class="no-grades"
            >
              <v-icon small color="grey" class="mr-1">mdi-school</v-icon>
              <span class="grey--text caption">No grades configured</span>
            </div>
          </div>
          <div v-else class="text-center pa-4">
            <v-progress-circular
              indeterminate
              color="#4caf50"
              size="24"
            ></v-progress-circular>
          </div>
        </v-card-text>

        <!-- Edit Grade View -->
        <v-card-text class="px-3 pt-1" v-else>
          <v-btn outline @click="backToGradesList" class="mb-3 ml-n2">
            <v-icon left>mdi-arrow-left</v-icon>
            Back to List
          </v-btn>

          <v-layout row wrap class="mb-3">
            <v-flex xs12 sm4 class="pr-2">
              <v-select
                v-model="currentGrade.branch"
                :items="branchList"
                label="Branch *"
                outlined
                dense
                :disabled="disableGradeSetupBranchSelection"
                @change="onGradeBranchChange"
              ></v-select>
            </v-flex>
            <v-flex xs12 sm4 class="pr-2">
              <v-select
                v-if="currentGrade.id"
                v-model="currentGrade.grade"
                :items="availableGrades"
                label="Grade *"
                outlined
                dense
                disabled
              ></v-select>
              <v-select
                v-else
                v-model="multipleGrades"
                :items="availableGrades"
                label="Grade(s) *"
                outlined
                dense
                multiple
                chips
                small-chips
                deletable-chips
                :disabled="!currentGrade.branch"
                :loading="loadingAvailableGrades"
                hint="Select one or more grades"
                persistent-hint
              ></v-select>
            </v-flex>
            <v-flex xs12 sm4>
              <v-switch
                v-model="currentGrade.allowBusChange"
                label="Allow Bus Change"
                color="success"
                hide-details
                class="mt-1"
              ></v-switch>
            </v-flex>
          </v-layout>

          <!-- Default Deadlines Section -->
          <div class="section-header mb-1">
            <v-icon small class="mr-1">mdi-clock-outline</v-icon>
            Default Deadlines (Sun - Fri)
          </div>

          <v-layout row wrap class="mb-3">
            <v-flex
              v-for="day in weekDays"
              :key="day.value"
              xs12
              sm4
              md2
              class="pa-1"
            >
              <v-card outlined class="pa-2">
                <div class="caption font-weight-bold mb-0">{{ day.label }}</div>
                <DxDateBox
                  type="time"
                  picker-type="list"
                  :interval="15"
                  :value="getDefaultDeadlineAsDate(day.value)"
                  @value-changed="
                    (e) => setDefaultDeadlineFromDate(day.value, e.value)
                  "
                  placeholder="Not set"
                  class="deadline-time-picker"
                />
                <v-menu
                  offset-y
                  v-if="getDaysWithDeadlines(day.value).length > 0"
                >
                  <template v-slot:activator="{ on }">
                    <span class="copy-from-link" v-on="on">
                      <v-icon x-small class="mr-1">mdi-content-copy</v-icon>
                      Copy from
                    </span>
                  </template>
                  <v-list dense>
                    <v-list-tile
                      v-for="sourceDay in getDaysWithDeadlines(day.value)"
                      :key="sourceDay.value"
                      @click="copyDeadlineFromDay(sourceDay.value, day.value)"
                    >
                      <v-list-tile-title>
                        {{ sourceDay.label }} ({{
                          formatTimeDisplay(
                            getDefaultDeadline(sourceDay.value)
                          )
                        }})
                      </v-list-tile-title>
                    </v-list-tile>
                  </v-list>
                </v-menu>
              </v-card>
            </v-flex>
          </v-layout>

          <!-- Exception Deadlines Section -->
          <div class="section-header mb-2 mt-4">
            <v-icon small class="mr-1">mdi-calendar</v-icon>
            Exception Deadlines
            <v-spacer></v-spacer>
            <v-btn small color="success" dark @click="addException">
              <v-icon x-small left>mdi-plus</v-icon>
              Add
            </v-btn>
          </div>

          <div class="exceptions-container" v-if="currentExceptions.length > 0">
            <div
              v-for="(exception, index) in currentExceptions.filter(
                (e) => !e._deleted
              )"
              :key="index"
              class="exception-chip"
            >
              <div class="exception-content">
                <v-icon small color="rgb(92, 184, 96)" class="mr-1"
                  >mdi-calendar</v-icon
                >
                <span class="exception-date">{{
                  formatExceptionDateShort(exception.exceptionDate)
                }}</span>
                <span class="exception-divider">•</span>
                <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                <span class="exception-time">{{
                  formatTimeDisplay(exception.deadline)
                }}</span>
              </div>
              <div class="exception-actions">
                <v-btn
                  icon
                  x-small
                  @click="editException(exception)"
                  class="action-btn"
                >
                  <v-icon x-small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  @click="removeException(exception)"
                  class="action-btn delete-btn"
                >
                  <v-icon x-small color="error">mdi-close</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
          <div v-else class="no-exceptions">
            <v-icon small color="grey" class="mr-1">mdi-calendar-blank</v-icon>
            <span class="grey--text caption">No exceptions configured</span>
          </div>

          <div v-if="showUnsavedWarning" class="unsaved-warning mt-3">
            <v-icon x-small color="orange" class="mr-1">mdi-alert</v-icon>
            <span>You have unsaved changes. Click "Save Grade" to apply.</span>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-3" v-if="editingGrade">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" dark @click="backToGradesList">
            Cancel
          </v-btn>
          <v-btn
            color="success"
            dark
            :loading="savingGradesSetup"
            @click="saveCurrentGrade"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save Grade
          </v-btn>
        </v-card-actions>
        <v-card-actions class="pa-3" v-else>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" dark @click="closeGradesSetupDialog">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add/Edit Exception Dialog -->
    <v-dialog v-model="exceptionDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">
          {{ editingException ? "Edit Exception" : "Add Exception" }}
        </v-card-title>
        <v-card-text>
          <v-menu
            v-model="exceptionDateMenu"
            :close-on-content-click="false"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="newException.exceptionDate"
                label="Date *"
                prepend-inner-icon="mdi-calendar"
                readonly
                outlined
                dense
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="newException.exceptionDate"
              color="rgb(92, 184, 96)"
              @input="exceptionDateMenu = false"
            ></v-date-picker>
          </v-menu>
          <div class="mb-3">
            <div class="caption mb-1">Deadline Time *</div>
            <DxDateBox
              type="time"
              picker-type="list"
              :interval="15"
              :value="getExceptionDeadlineAsDate()"
              @value-changed="setExceptionDeadlineFromDate"
              placeholder="Select time"
              class="deadline-time-picker"
            />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="exceptionDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="success" dark @click="saveException">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Grade Confirmation -->
    <v-dialog v-model="deleteGradeDialog" max-width="400">
      <v-card>
        <v-card-title class="headline error white--text">
          <v-icon left dark>mdi-alert-circle</v-icon>
          Delete Grade
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outline value="true" class="mb-0 pa-2">
            Are you sure you want to delete this grade? This will also delete
            all associated deadlines.
          </v-alert>
          <div class="mt-3 subheading-1" v-if="gradeToDelete">
            <span class="font-weight-medium">Grade:</span>
            {{ gradeToDelete.grade }} ({{ gradeToDelete.branch }})
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="deleteGradeDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="error"
            dark
            :loading="deletingGrade"
            @click="deleteGrade"
            >Delete</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk Add Exception Dialog -->
    <v-dialog v-model="bulkExceptionDialog" max-width="450">
      <v-card>
        <v-card-title class="headline primary white--text">
          <v-icon left dark>mdi-calendar-plus</v-icon>
          Add Exception to Selected Grades
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="mb-3">
            <div class="caption grey--text mb-2">
              Selected Grades ({{ selectedGrades.length }})
            </div>
            <div class="selected-grades-list">
              <div
                v-for="grade in getSelectedGradeDetails()"
                :key="grade.id"
                class="selected-grade-item"
              >
                <span class="grade-text">
                  <strong>{{ grade.grade }}</strong>
                  <span class="grey--text ml-2">({{ grade.branch }})</span>
                </span>
              </div>
            </div>
          </div>
          <v-menu
            v-model="bulkExceptionDateMenu"
            :close-on-content-click="false"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="bulkException.exceptionDate"
                label="Date *"
                prepend-inner-icon="mdi-calendar"
                readonly
                outlined
                dense
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="bulkException.exceptionDate"
              color="rgb(92, 184, 96)"
              @input="bulkExceptionDateMenu = false"
            ></v-date-picker>
          </v-menu>
          <div class="mb-3">
            <div class="caption mb-1">Deadline Time *</div>
            <DxDateBox
              type="time"
              picker-type="list"
              :interval="15"
              :value="getBulkExceptionDeadlineAsDate()"
              @value-changed="setBulkExceptionDeadlineFromDate"
              placeholder="Select time"
              class="deadline-time-picker"
            />
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="bulkExceptionDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="success"
            dark
            :loading="savingBulkException"
            @click="saveBulkException"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save Exception
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Account Search Dialog -->
    <v-dialog v-model="accountSearchDialog" max-width="800">
      <v-card>
        <v-card-title class="dialog-header">
          <v-icon class="mr-3" color="white" size="28"
            >mdi-account-search</v-icon
          >
          <span class="dialog-title">Search Account</span>
          <v-spacer></v-spacer>
          <v-btn
            class="white--text"
            icon
            dark
            @click="closeAccountSearchDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Phone Number Search -->
          <v-layout row wrap class="mb-1">
            <v-flex xs12 class="pr-2">
              <v-text-field
                v-model="dialogPhoneSearch"
                label="Search by Phone Number"
                prepend-inner-icon="mdi-phone"
                outlined
                dense
                clearable
                mask="###-###-####"
                @input="searchDialogByPhone"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Search Name Mode Toggle -->
          <v-layout row wrap align-center class="mb-2">
            <v-flex xs12 class="pr-2">
              <div class="caption grey--text mb-1">Or Search By Name</div>
              <v-btn-toggle
                v-model="dialogSearchNameMode"
                mandatory
                class="search-toggle"
              >
                <v-btn flat small value="yiddish">אידיש</v-btn>
                <v-btn flat small value="english">English</v-btn>
              </v-btn-toggle>
            </v-flex>
          </v-layout>

          <!-- Account Search Fields -->
          <v-layout row wrap class="mb-3">
            <v-flex xs12 sm6 class="pr-2">
              <v-text-field
                v-model="dialogLastNameSearch"
                :label="
                  dialogSearchNameMode === 'yiddish'
                    ? 'לעצטע נאמען'
                    : 'Last Name'
                "
                prepend-inner-icon="mdi-account"
                outlined
                dense
                clearable
                @input="searchDialogByName"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6>
              <v-text-field
                v-model="dialogFirstNameSearch"
                :label="
                  dialogSearchNameMode === 'yiddish'
                    ? 'טאטעס נאמען'
                    : 'Father Name'
                "
                prepend-inner-icon="mdi-account"
                outlined
                dense
                clearable
                @input="searchDialogByName"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Address Search -->
          <v-layout row wrap class="mb-2">
            <v-flex xs12>
              <div class="caption grey--text mb-1">Or Search By Address</div>
            </v-flex>
          </v-layout>
          <v-layout row wrap class="mb-3">
            <v-flex xs12 sm6 class="pr-2">
              <v-text-field
                v-model="dialogHouseNumberSearch"
                label="House Number"
                prepend-inner-icon="mdi-home"
                outlined
                dense
                clearable
                @input="searchDialogByAddress"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm6>
              <v-text-field
                v-model="dialogStreetSearch"
                label="Street Name"
                prepend-inner-icon="mdi-road"
                outlined
                dense
                clearable
                @input="searchDialogByAddress"
                autocomplete="no-password"
              ></v-text-field>
            </v-flex>
          </v-layout>

          <!-- Account List -->
          <div class="account-list-container" v-if="dialogAccounts.length > 0">
            <div class="caption grey--text mb-2 px-2">
              Select an account ({{ dialogAccounts.length }} found)
            </div>
            <v-list dense class="account-list">
              <v-list-tile
                v-for="account in dialogAccounts"
                :key="account.accountID"
                @click="onAccountSelected(account)"
                class="account-item"
              >
                <v-list-tile-avatar>
                  <v-icon color="rgb(92, 184, 96)">mdi-account-box</v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content>
                  <v-list-tile-title>
                    <strong>{{ account.name }}</strong>
                    <span class="grey--text ml-2">({{ account.hname }})</span>
                  </v-list-tile-title>
                  <v-list-tile-sub-title>
                    Account ID: {{ account.accountID }}
                    <span v-if="account.homePhone" class="ml-1">
                      | {{ account.homePhone }}
                    </span>
                    <span v-if="account.cellPhone" class="ml-1">
                      | {{ account.cellPhone }}
                    </span>
                    <span v-if="account.address1" class="ml-1">
                      | {{ account.address1 }}
                    </span>
                    <span v-if="account.busAddress1" class="ml-1">
                      | Bus: {{ account.busAddress1 }}
                    </span>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
                <v-list-tile-action>
                  <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
                </v-list-tile-action>
              </v-list-tile>
            </v-list>
          </div>

          <!-- No Accounts Message -->
          <v-alert
            v-if="dialogShowNoAccountsMessage && dialogAccounts.length === 0"
            type="warning"
            outline
            dense
            value="true"
            class="mb-3"
          >
            No accounts found. Please try a different search.
          </v-alert>

          <!-- Empty state -->
          <div
            v-if="!dialogShowNoAccountsMessage && dialogAccounts.length === 0"
            class="text-center px-2 pb-3 grey--text"
          >
            <v-icon large color="grey lighten-1">mdi-account-search</v-icon>
            <div class="mt-2">
              Search for an account by phone, name, or address
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Incomplete Route Confirmation Dialog -->
    <v-dialog v-model="incompleteRouteDialog" max-width="500">
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-alert</v-icon>
          Incomplete Route Selection
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outline value="true" class="mb-0 pa-2">
            You have only selected the
            {{ selectedPickupRoute ? "Pickup" : "Dropoff" }} route.
          </v-alert>
          <div class="mt-3">Do you want to continue saving anyway?</div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="incompleteRouteDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="warning" dark @click="confirmSaveWithIncompleteRoute">
            Continue Saving
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Notification Confirmation Dialog -->
    <v-dialog v-model="notificationConfirmDialog" max-width="550" persistent>
      <v-card>
        <v-card-title class="headline success white--text">
          <v-icon left dark>mdi-information</v-icon>
          Notification Will Be Sent
        </v-card-title>
        <v-card-text class="pt-3">
          <v-alert value="true" type="info" outline class="mb-3">
            <div class="font-weight-bold mb-1">
              <v-icon small color="info" class="mr-1">
                {{
                  newBusChange.notificationType === "CALL"
                    ? "mdi-phone"
                    : "mdi-message-text"
                }}
              </v-icon>
              {{
                newBusChange.notificationType === "CALL"
                  ? "A phone call"
                  : "A text message"
              }}
              will automatically be sent to:
            </div>
            <div class="ml-4">
              {{ formatPhoneNumber(newBusChange.notificationPhoneNumber) }}
            </div>
          </v-alert>
          <div class="grey--text text--darken-1">
            The bus change will be saved and the notification will be sent
            automatically.
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="cancelSave">Cancel</v-btn>
          <v-btn
            v-if="!isSourceMandatory"
            color="success"
            outline
            @click="proceedWithoutNotification"
          >
            Save Without Notification
          </v-btn>
          <v-btn color="success" dark @click="proceedWithNotification">
            <v-icon left small>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Missing Notification Fields Dialog -->
    <v-dialog
      v-model="missingNotificationFieldsDialog"
      max-width="550"
      persistent
    >
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-alert-circle</v-icon>
          Missing Notification Information
        </v-card-title>
        <v-card-text class="pt-3">
          <v-alert type="warning" outline value="true" class="mb-3">
            <div class="font-weight-bold mb-2">
              Cannot send notification - missing required fields:
            </div>
            <ul class="ml-4">
              <li v-if="!newBusChange.notificationPhoneNumber">
                Notification Phone Number
              </li>
              <li v-if="!newBusChange.notificationType">
                Notification Type
              </li>
            </ul>
          </v-alert>
          <div class="mt-3">
            Would you like to fill in these fields or save without sending a
            notification?
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="cancelSave">Cancel</v-btn>
          <v-btn color="warning" @click="saveWithoutNotification">
            Save Without Notification
          </v-btn>
          <v-btn color="success" dark @click="closeMissingFieldsDialog">
            Fill in Fields
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cleared Notification Warning Dialog (IVR/ONLINE) -->
    <v-dialog
      v-model="clearedNotificationWarningDialog"
      max-width="550"
      persistent
    >
      <v-card>
        <v-card-title class="headline warning white--text">
          <v-icon left dark>mdi-alert</v-icon>
          Notification Fields Cleared
        </v-card-title>
        <v-card-text class="pt-3">
          <v-alert type="warning" outline value="true" outlined class="mb-3">
            <div class="font-weight-bold mb-2 grey--text text--darken-4">
              This record originally had notification information
            </div>
            <div class="grey--text text--darken-3">
              By clearing the notification fields, the user will NOT receive a
              notification about this bus change.
            </div>
          </v-alert>
          <div class="mt-3">
            Do you want to continue saving without notification?
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="cancelClearedNotificationWarning">
            Cancel
          </v-btn>
          <v-btn color="warning" dark @click="confirmSaveWithoutNotification">
            Save Without Notification
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Report Download Dialog -->
    <v-dialog v-model="reportDialog" max-width="1200" persistent scrollable>
      <v-card>
        <v-card-title class="dialog-header">
          <v-icon class="mr-3" color="white" size="28"
            >mdi-file-download</v-icon
          >
          <span class="dialog-title">Download Bus Change Report</span>
          <v-spacer></v-spacer>
          <v-btn class="white--text" icon dark @click="closeReportDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Filters - All in One Line -->
          <v-layout row align-center class="mb-2 report-filters-row">
            <!-- Branch Selection -->
            <v-flex
              :md="showReportBranchField ? 2 : 0"
              class="pr-2"
              v-if="showReportBranchField"
            >
              <v-autocomplete
                v-model="reportFilterBranch"
                :items="reportBranches"
                label="Branch"
                prepend-inner-icon="mdi-source-branch"
                outlined
                dense
                clearable
                hide-details
                @change="applyReportFilters"
              ></v-autocomplete>
            </v-flex>

            <!-- Class Filter -->
            <v-flex :md="showReportBranchField ? 2 : 3" class="pr-2">
              <v-autocomplete
                v-model="reportFilterClass"
                :items="reportClasses"
                label="Grade"
                prepend-inner-icon="mdi-chair-school"
                outlined
                dense
                clearable
                multiple
                chips
                deletable-chips
                small-chips
                hide-details
                :no-data-text="
                  reportFilterBranch
                    ? 'No grades available'
                    : 'Please select branch first'
                "
                @change="applyReportFilters"
              ></v-autocomplete>
            </v-flex>

            <!-- Bus Number Filter -->
            <v-flex :md="showReportBranchField ? 2 : 3" class="pr-2">
              <v-autocomplete
                v-model="reportFilterBusNumber"
                :items="reportBusNumbers"
                label="Bus #"
                prepend-inner-icon="mdi-bus"
                outlined
                dense
                clearable
                multiple
                chips
                deletable-chips
                small-chips
                hide-details
                @change="applyReportFilters"
              ></v-autocomplete>
            </v-flex>

            <!-- Start Date Filter -->
            <v-flex :md="showReportBranchField ? 3 : 3" class="pr-2">
              <v-menu
                v-model="reportStartDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="reportFilterStartDate"
                    label="Start Date"
                    prepend-inner-icon="mdi-calendar"
                    outlined
                    dense
                    clearable
                    readonly
                    hide-details
                    v-on="on"
                    @click:clear="clearStartDateFilter"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="reportFilterStartDate"
                  no-title
                  @input="
                    reportStartDateMenu = false;
                    applyReportFilters();
                  "
                ></v-date-picker>
              </v-menu>
            </v-flex>

            <!-- End Date Filter -->
            <v-flex :md="showReportBranchField ? 3 : 3">
              <v-menu
                v-model="reportEndDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="reportFilterEndDate"
                    label="End Date"
                    prepend-inner-icon="mdi-calendar"
                    outlined
                    dense
                    clearable
                    readonly
                    hide-details
                    v-on="on"
                    @click:clear="clearEndDateFilter"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="reportFilterEndDate"
                  no-title
                  @input="
                    reportEndDateMenu = false;
                    applyReportFilters();
                  "
                ></v-date-picker>
              </v-menu>
            </v-flex>
          </v-layout>

          <!-- Bus Changes Selection Table -->
          <div class="report-table-container mt-3">
            <div class="report-table-header">
              <span class="body-2">
                <strong>{{ selectedReportBusChanges.length }}</strong> of
                <strong>{{ filteredReportBusChanges.length }}</strong> selected
              </span>
              <div class="d-flex align-center">
                <v-checkbox
                  v-model="showCompletedBusChanges"
                  label="Show Completed"
                  color="success"
                  hide-details
                  class="mt-0 pt-0 mr-3"
                  @change="applyReportFilters"
                ></v-checkbox>
                <v-btn
                  small
                  flat
                  class="my-0"
                  color="success"
                  @click="selectAllReportBusChanges"
                  :disabled="filteredReportBusChanges.length === 0"
                >
                  Select All
                </v-btn>
                <v-btn
                  flat
                  small
                  class="my-0"
                  @click="clearAllReportBusChanges"
                  :disabled="selectedReportBusChanges.length === 0"
                >
                  Clear
                </v-btn>
              </div>
            </div>
            <div class="report-table-scroll">
              <v-data-table
                v-model="selectedReportBusChanges"
                :headers="reportBusChangesHeaders"
                :items="filteredReportBusChanges"
                :loading="loadingReportBusChanges"
                item-key="id"
                select-all
                :pagination.sync="reportPagination"
                class="elevation-1 report-data-table"
                hide-actions
              >
                <template v-slot:items="props">
                  <td>
                    <v-checkbox
                      v-model="props.selected"
                      color="success"
                      hide-details
                    ></v-checkbox>
                  </td>
                  <td>
                    <div class="bus-change-main-info">
                      {{ props.item.studentNameHeb || props.item.studentName }}
                    </div>
                    <div class="bus-change-sub-info">
                      <span v-if="props.item.busChangeName"
                        ><v-icon x-small>mdi-account</v-icon>
                        {{ props.item.busChangeName }}</span
                      >
                      <span v-if="props.item.busChangeAddress"
                        ><v-icon x-small>mdi-map-marker</v-icon>
                        {{ props.item.busChangeAddress }}</span
                      >
                      <span v-if="props.item.status">
                        <v-icon
                          x-small
                          :color="getStatusIconColor(props.item.status)"
                          >{{ getStatusIcon(props.item.status) }}</v-icon
                        >
                        {{ props.item.status }}
                      </span>
                    </div>
                  </td>
                  <td>{{ props.item.branch }}</td>
                  <td>{{ props.item.class }}</td>
                  <td>{{ props.item.busNumber }}</td>
                  <td>{{ formatDate(props.item.startDate) }}</td>
                  <td>{{ formatDate(props.item.endDate) }}</td>
                </template>
              </v-data-table>
            </div>
            <div class="report-table-pagination">
              <v-pagination
                v-if="reportPaginationPages > 1"
                v-model="reportPagination.page"
                :length="reportPaginationPages"
                :total-visible="7"
                color="success"
              ></v-pagination>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="pb-3 pt-2">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="closeReportDialog">Cancel</v-btn>
          <v-btn
            color="success"
            @click="downloadReport('standard')"
            :loading="downloadingReport === 'standard'"
            :disabled="
              selectedReportBusChanges.length === 0 ||
                (downloadingReport && downloadingReport !== 'standard')
            "
          >
            <v-icon left>mdi-file-document</v-icon>
            Standard Report ({{ selectedReportBusChanges.length }})
          </v-btn>
          <v-btn
            color="success"
            @click="downloadReport('detailed')"
            :loading="downloadingReport === 'detailed'"
            :disabled="
              selectedReportBusChanges.length === 0 ||
                (downloadingReport && downloadingReport !== 'detailed')
            "
          >
            <v-icon left>mdi-file-document-multiple</v-icon>
            Detailed Report ({{ selectedReportBusChanges.length }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import DxDateBox from "devextreme-vue/date-box";

export default {
  components: {
    DxDateBox,
  },
  data: () => ({
    title: "Bus Changes",
    actions: [],
    url: "",

    // Dialog state
    addDialog: false,
    saving: false,
    loadingStudents: false,
    isEditing: false,
    editingId: null,
    editingRecordInfo: null,
    busChangeComments: [],
    loadingBusChangeComments: false,

    // Account search
    phoneSearch: "",
    firstNameSearch: "",
    lastNameSearch: "",
    houseNumberSearch: "",
    streetSearch: "",
    accounts: [],
    selectedAccount: null,
    searchNameMode: "yiddish",
    showNoAccountsMessage: false,
    searchDebounceTimer: null,

    // Student selection
    students: [],
    selectedStudent: null,
    branchList: [],

    // Grades setup
    gradeError: null,
    selectedGradeDeadlines: [], // Cached deadlines for selected student's grade
    gradesSetupDialog: false,
    gradesSetupSearch: "",
    gradesSetupBranchFilter: null,
    loadingGradesSetup: false,
    savingGradesSetup: false,
    busChangeGrades: [],
    editingGrade: false,
    currentGrade: {
      id: null,
      grade: "",
      branch: "",
      allowBusChange: true,
    },
    currentDeadlines: [],
    currentExceptions: [],
    selectedGrades: [], // For bulk exception
    bulkExceptionDialog: false,
    bulkExceptionDateMenu: false,
    savingBulkException: false,
    bulkException: { exceptionDate: "", deadline: "" },
    gradesSetupHeaders: [
      { text: "Grade", value: "grade", sortable: true },
      { text: "Branch", value: "branch", sortable: true },
      {
        text: "Allow Bus Change",
        value: "allowBusChange",
        sortable: false,
        align: "center",
      },
      {
        text: "Actions",
        value: "actions",
        sortable: false,
        align: "center",
        width: "120px",
      },
    ],
    exceptionHeaders: [
      { text: "Date", value: "exceptionDate", sortable: true },
      { text: "Deadline", value: "deadline", sortable: false },
      {
        text: "Actions",
        value: "actions",
        sortable: false,
        align: "center",
        width: "100px",
      },
    ],
    weekDays: [
      { value: 0, label: "Sunday" },
      { value: 1, label: "Monday" },
      { value: 2, label: "Tuesday" },
      { value: 3, label: "Wednesday" },
      { value: 4, label: "Thursday" },
      { value: 5, label: "Friday" },
    ],
    exceptionDialog: false,
    exceptionDateMenu: false,
    editingException: null,
    newException: { exceptionDate: "", deadline: "" },
    showUnsavedWarning: false,
    deleteGradeDialog: false,
    deletingGrade: false,
    gradeToDelete: null,
    availableGrades: [],
    loadingAvailableGrades: false,
    multipleGrades: [], // For adding multiple grades at once

    // Bus Routes selection
    busRoutes: [],
    pickupRoutes: [],
    dropoffRoutes: [],
    selectedPickupRoute: null,
    selectedDropoffRoute: null,
    pickupRouteSearch: null,
    dropoffRouteSearch: null,
    loadingBusRoutes: false,

    // Delete dialog
    deleteDialog: false,
    deleting: false,
    recordToDelete: null,

    // Account search dialog
    accountSearchDialog: false,
    dialogPhoneSearch: "",
    dialogFirstNameSearch: "",
    dialogLastNameSearch: "",
    dialogHouseNumberSearch: "",
    dialogStreetSearch: "",
    dialogAccounts: [],
    dialogSearchNameMode: "yiddish",
    dialogShowNoAccountsMessage: false,
    dialogSearchDebounceTimer: null,

    // Incomplete route confirmation dialog
    incompleteRouteDialog: false,
    skipRouteWarning: false,

    // Bus change notification dialogs and state
    notificationConfirmDialog: false,
    missingNotificationFieldsDialog: false,
    clearedNotificationWarningDialog: false,
    skipNotification: false,
    originalPickupRouteID: null,
    originalDropoffRouteID: null,
    originalNotificationPhone: null,
    originalNotificationType: null,
    savedBusChangeId: null,
    sendingNotification: false,

    // Track if start date was edited
    startDateEdited: false,

    // Date pickers
    startDateMenu: false,
    endDateMenu: false,

    // Report download dialog
    reportDialog: false,
    reportBranches: [],
    reportClasses: [],
    reportBusNumbers: [],

    // Report filters
    reportFilterBranch: null,
    reportFilterClass: [],
    reportFilterBusNumber: [],
    reportFilterStartDate: null,
    reportFilterEndDate: null,
    reportStartDateMenu: false,
    reportEndDateMenu: false,
    showCompletedBusChanges: false, // Default to hide completed

    // Report bus changes data
    reportBusChanges: [], // All bus changes from server (display-friendly)
    reportBusChangesFullData: [], // Full original odata response
    filteredReportBusChanges: [], // Filtered bus changes
    selectedReportBusChanges: [], // Selected bus changes for report
    loadingReportBusChanges: false,
    reportPagination: {
      page: 1,
      rowsPerPage: 10,
      rowsPerPageItems: [10, 25, 50, 100],
    },
    reportBusChangesHeaders: [
      { text: "Student Name", value: "studentName", sortable: true },
      { text: "Branch", value: "branch", sortable: true },
      { text: "Class", value: "class", sortable: true },
      { text: "Bus Number", value: "busNumber", sortable: true },
      { text: "Start Date", value: "startDate", sortable: true },
      { text: "End Date", value: "endDate", sortable: true },
    ],

    loadingReportOptions: false,
    downloadingReport: null, // Track which report is downloading: 'standard', 'detailed', or null

    // New bus change data
    newBusChange: {
      startDate: "",
      endDate: "",
      startPeriod: "",
      endPeriod: "",
      busChangeAddress: "",
      busChangePhoneNumber: "",
      geoPhoneNumber: "",
      busChangeName: "",
      busChangeAccountID: "",
      notificationPhoneNumber: "",
      notificationType: "",
      note: "",
    },
    periodOptions: [
      { label: "Morning Pickup", value: "AM" },
      { label: "Afternoon Dropoff", value: "PM" },
    ],

    rules: {
      required: (value) => !!value || "Required.",
      phoneNumber: (v) => {
        if (!v) return true;
        const digits = v.replace(/[-\s()]/g, "");
        return (
          ([10, 11].includes(digits.length) && /^\d+$/.test(digits)) ||
          "Invalid Phone Number"
        );
      },
    },

    columns: [
      {
        field: "statusIcon",
        label: "Status",
        isIcon: true,
        template: "icon",
        alignment: "center",
        visible: true,
        allowFiltering: false,
        allowHeaderFiltering: false,
        allowHiding: false,
        allowSearch: false,
        cssClass: "width-25",
        icon: (value) => {
          if (!value) return "";
          switch (value.trim().toUpperCase()) {
            case "NEW":
              return "mdi-new-box";
            case "FUTURE":
            case "PENDING":
              return "mdi-clock-outline";
            case "ACTIVE":
              return "mdi-check-circle";
            case "COMPLETED":
              return "mdi-check-all";
            default:
              return "";
          }
        },
        iconColor: (value) => {
          if (!value) return "";
          switch (value.trim().toUpperCase()) {
            case "NEW":
              return "#2196f3";
            case "FUTURE":
            case "PENDING":
              return "#ff9800";
            case "ACTIVE":
              return "rgb(92, 184, 96)";
            case "COMPLETED":
              return "#9e9e9e";
            default:
              return "";
          }
        },
      },
      {
        label: "Student ID",
        field: "studentID",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Student Name",
        field: "studentName",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Student Name (Heb)",
        field: "studentNameHeb",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Family ID",
        field: "familyID",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Building",
        field: "building",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Branch",
        field: "branch",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Class",
        field: "class",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Section",
        field: "section",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Term",
        field: "term",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Start Date",
        field: "startDate",
        visible: true,
        type: "date",
        format: "MM/dd/yyyy",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
        sortIndex: 1,
        sortOrder: "desc",
      },
      {
        label: "Start Period",
        field: "startPeriod",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "End Date",
        field: "endDate",
        visible: true,
        type: "date",
        format: "MM/dd/yyyy",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "End Period",
        field: "endPeriod",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Home Address",
        field: "homeAddress",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Bus Change Address",
        field: "busChangeAddress",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Bus Change Description",
        field: "busChangeDescription",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Bus Change Phone",
        field: "busChangePhoneNumber",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Geo Phone Number",
        field: "geoPhoneNumber",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Pickup Route ID",
        field: "pickupRouteID",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Dropoff Route ID",
        field: "dropoffRouteID",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Note",
        field: "note",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Bus Change Name",
        field: "busChangeName",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Bus Change Account ID",
        field: "busChangeAccountID",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Notification Phone",
        field: "notificationPhoneNumber",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Notification Type",
        field: "notificationType",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Status",
        field: "status",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
        sortIndex: 0,
        sortOrder: "asc",
        calculateSortValue: (data) => {
          const order = { NEW: 1, ACTIVE: 2, FUTURE: 3, PENDING: 3, COMPLETED: 4 };
          const status = data && data.status ? data.status.toUpperCase() : "";
          return order[status] || 5;
        },
      },
      {
        label: "Father Name",
        field: "fatherName",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Father Name (Heb)",
        field: "fatherNameH",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Email",
        field: "emailAddress",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Home Phone",
        field: "homePhone",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Cell Phone",
        field: "cellPhone",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Source",
        field: "source",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Entered By",
        field: "enteredBy",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Entered On",
        field: "enteredOn",
        visible: true,
        type: "date",
        format: "MM/dd/yyyy hh:mm a",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
    ],
  }),

  computed: {
    permittedBranches() {
      if (!this.userBranch) return [];
      return this.userBranch
        .split(",")
        .map((b) => b.trim())
        .filter(Boolean);
    },
    userBranch() {
      const branch = this.$store.state.authentication.branch;
      if (branch && !branch.includes("null")) {
        return branch;
      }
      return null;
    },
    disableGradeSetupBranchSelection() {
      if (this.currentGrade.id) return true;
      if (this.isAdmin()) return false;
      return this.permittedBranches.length <= 1;
    },
    deadlineWarningMessage() {
      if (!this.selectedStudent) return null;
      if (!this.startDateEdited) return null;
      return this.checkDeadlineWarning(
        this.selectedStudent.class,
        this.selectedStudent.branch,
        this.newBusChange.startDate
      );
    },
    filteredBusChangeGrades() {
      let grades = this.busChangeGrades;
      if (!this.isAdmin()) {
        // Handle comma-separated branches like "2411,2412"
        const allowedBranches = this.userBranch.split(",").map((b) => b.trim());
        grades = grades.filter((g) => allowedBranches.includes(g.branch));
      } else if (this.gradesSetupBranchFilter) {
        grades = grades.filter(
          (g) => g.branch === this.gradesSetupBranchFilter
        );
      }
      return grades;
    },
    filteredBusChangeGradesSearched() {
      let grades = this.filteredBusChangeGrades;
      if (this.gradesSetupSearch) {
        const search = this.gradesSetupSearch.toLowerCase();
        grades = grades.filter(
          (g) =>
            (g.grade && g.grade.toLowerCase().includes(search)) ||
            (g.branch && g.branch.toLowerCase().includes(search))
        );
      }
      return grades;
    },
    filteredPickupRoutes() {
      let routes = this.pickupRoutes;

      // Filter by selected student's building
      if (this.selectedStudent && this.selectedStudent.building) {
        const studentBuilding = this.selectedStudent.building.toString();
        routes = routes.filter((r) => {
          if (!r.buildings) return false;
          // buildings can be comma-separated like "75,212"
          const routeBuildings = r.buildings.split(",").map((b) => b.trim());
          return routeBuildings.includes(studentBuilding);
        });
      }

      return routes;
    },
    filteredDropoffRoutes() {
      let routes = this.dropoffRoutes;

      // Filter by selected student's building
      if (this.selectedStudent && this.selectedStudent.building) {
        const studentBuilding = this.selectedStudent.building.toString();
        routes = routes.filter((r) => {
          if (!r.buildings) return false;
          // buildings can be comma-separated like "75,212"
          const routeBuildings = r.buildings.split(",").map((b) => b.trim());
          return routeBuildings.includes(studentBuilding);
        });
      }

      return routes;
    },
    isSourceMandatory() {
      // Check if the source requires mandatory notification
      if (!this.editingRecordInfo || !this.editingRecordInfo.source) {
        return false;
      }
      const source = this.editingRecordInfo.source.toUpperCase();
      return source === "IVR" || source === "ONLINE";
    },
    showReportBranchField() {
      // Show branch field if user has multiple branches or is admin
      return !this.userBranch || this.userBranch.includes(",");
    },
    reportPaginationPages() {
      if (!this.filteredReportBusChanges.length) return 1;
      return Math.ceil(
        this.filteredReportBusChanges.length / this.reportPagination.rowsPerPage
      );
    },
    sortedReportBusChanges() {
      // Return filtered bus changes in the current sort order from the data table
      const items = [...this.filteredReportBusChanges];

      if (this.reportPagination.sortBy) {
        const sortKey = this.reportPagination.sortBy;
        const descending = this.reportPagination.descending;

        items.sort((a, b) => {
          let aVal = a[sortKey];
          let bVal = b[sortKey];

          // Handle null/undefined values
          if (aVal == null) aVal = "";
          if (bVal == null) bVal = "";

          // Handle date values
          if (sortKey === "startDate" || sortKey === "endDate") {
            aVal = new Date(aVal).getTime() || 0;
            bVal = new Date(bVal).getTime() || 0;
          }

          // Handle numeric values
          if (sortKey === "busNumber") {
            const aNum = parseInt(aVal);
            const bNum = parseInt(bVal);
            if (!isNaN(aNum) && !isNaN(bNum)) {
              aVal = aNum;
              bVal = bNum;
            }
          }

          // Compare values
          if (aVal < bVal) return descending ? 1 : -1;
          if (aVal > bVal) return descending ? -1 : 1;
          return 0;
        });
      }

      return items;
    },
  },

  watch: {
    searchNameMode() {
      // Re-search when switching between English and Yiddish
      if (this.firstNameSearch || this.lastNameSearch) {
        this.searchByName();
      }
    },
    dialogSearchNameMode() {
      // Re-search when switching between English and Yiddish in dialog
      if (this.dialogFirstNameSearch || this.dialogLastNameSearch) {
        this.searchDialogByName();
      }
    },
    reportFilterBranch(newBranch) {
      // Reload classes when branch changes
      if (newBranch) {
        this.reportClasses = [];
        this.reportFilterClass = [];
        this.loadReportClasses();
      }
    },
  },

  methods: {
    async beforeSetupGrid() {
      this.url =
        "busChange?" +
        (this.userBranch ? "branch=" + this.userBranch : "branch=") +
        "&includeCompleted=false";

      this.actions.push({
        text: "Add Bus Change",
        action: this.openAddDialog,
        icon: "mdi-plus",
      });

      this.actions.push({
        text: "Grades Setup",
        action: this.openGradesSetupDialog,
        icon: "mdi-cog",
      });

      this.actions.push({
        text: "Show Completed",
        type: "switch",
        align: "left",
        action: this.onShowCompletedChanged,
      });

      this.actions.push({
        text: "Download Report",
        action: this.openReportDialog,
        icon: "mdi-download",
      });
    },

    onShowCompletedChanged(value) {
      const branchParam = this.userBranch
        ? "branch=" + this.userBranch
        : "branch=";
      if (value) {
        this.url = "busChange?" + branchParam + "&includeCompleted=true";
      } else {
        this.url = "busChange?" + branchParam + "&includeCompleted=false";
      }
      this.$refs.grid.reloadDataSource(this.url);
    },

    isAdmin() {
      const isAdmin = this.$store.getters["authentication/isAdmin"];
      if (!isAdmin || isAdmin == "false") return false;
      return true;
    },

    customRouteFilter(item, queryText, itemText) {
      if (!queryText) return true;

      const searchUpper = queryText.toUpperCase();
      const route = item;

      return (
        (route.description &&
          route.description.toUpperCase().includes(searchUpper)) ||
        (route.busNumber &&
          route.busNumber
            .toString()
            .toUpperCase()
            .includes(searchUpper)) ||
        (route.driver && route.driver.toUpperCase().includes(searchUpper)) ||
        (route.color && route.color.toUpperCase().includes(searchUpper)) ||
        (route.timeStart &&
          route.timeStart.toUpperCase().includes(searchUpper)) ||
        (route.routeIdentifier &&
          route.routeIdentifier.toUpperCase().includes(searchUpper))
      );
    },

    getRouteColor(colorName) {
      if (!colorName) return null;

      const colorUpper = colorName.toUpperCase().trim();
      const colorMap = {
        BEIGE: "#F5F5DC",
        BLACK: "#000000",
        BLUE: "#0000FF",
        BROWN: "#8B4513",
        GOLD: "#FFD700",
        GREEN: "#008000",
        ORANGE: "#FFA500",
        PINK: "#FFC0CB",
        PURPLE: "#800080",
        RED: "#FF0000",
        SILVER: "#C0C0C0",
        WHITE: "#E0E0E0",
        YELLOW: "#FFFF00",
        "DARK BLUE": "#00008B",
        "DARK GREEN": "#006400",
        "DARK PINK": "#FF1493",
        "DARK PURPLE": "#4B0082",
        "LIGHT BLUE": "#ADD8E6",
        "LIGHT GREEN": "#90EE90",
        "LIGHT PINK": "#FFB6C1",
        "LIGHT PURPLE": "#DDA0DD",
        GRAY: "#808080",
        GREY: "#808080",
        CYAN: "#00FFFF",
        TEAL: "#008080",
        LIME: "#00FF00",
        INDIGO: "#4B0082",
        NAVY: "#000080",
        MAROON: "#800000",
        OLIVE: "#808000",
        VIOLET: "#EE82EE",
        TURQUOISE: "#40E0D0",
        CORAL: "#FF7F50",
        SALMON: "#FA8072",
        MINT: "#98FF98",
        LAVENDER: "#E6E6FA",
        PEACH: "#FFE5B4",
        SKY: "#87CEEB",
        ROSE: "#FF007F",
      };

      return colorMap[colorUpper] || "#5cb860"; // Green default instead of white
    },

    getRouteChipStyle(route) {
      // Default style when no route or no color
      if (!route || !route.color) {
        return {
          background: "#5cb860", // Green default
          color: "#FFFFFF",
          border: "1px solid rgba(0, 0, 0, 0.2)",
        };
      }

      const bgColor = this.getRouteColor(route.color);
      if (!bgColor) {
        return {
          background: "#5cb860", // Green default
          color: "#FFFFFF",
          border: "1px solid rgba(0, 0, 0, 0.2)",
        };
      }

      // Determine if we need light or dark text based on background
      const colorUpper = route.color.toUpperCase().trim();
      const isLightBackground = [
        "BEIGE",
        "WHITE",
        "YELLOW",
        "LIGHT BLUE",
        "LIGHT GREEN",
        "LIGHT PINK",
        "LIGHT PURPLE",
        "SILVER",
        "GOLD",
        "PINK",
        "ORANGE",
        "LAVENDER",
        "PEACH",
        "MINT",
      ].includes(colorUpper);

      // For white or very light backgrounds, use a more visible style
      if (
        colorUpper === "WHITE" ||
        bgColor.toUpperCase() === "#FFF" ||
        bgColor.toUpperCase() === "#FFFFFF"
      ) {
        return {
          background: "#f5f5f5",
          color: "#000000",
          border: "2px solid #cccccc",
        };
      }

      return {
        background: bgColor,
        color: isLightBackground ? "#000000" : "#FFFFFF",
        border: isLightBackground
          ? "1.5px solid rgba(0, 0, 0, 0.3)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      };
    },

    async openAddDialog() {
      this.addDialog = true;
      this.resetForm();
      await Promise.all([
        this.loadBranches(),
        this.loadBusChangeGrades(),
        this.loadBusRoutes(),
      ]);
    },

    async onRowClicked(row) {
      this.resetForm();

      // Load bus change grades and bus routes if not loaded
      const loadPromises = [];
      if (!this.busChangeGrades.length) {
        loadPromises.push(this.loadBusChangeGrades());
      }
      if (!this.busRoutes.length) {
        loadPromises.push(this.loadBusRoutes());
      }
      if (loadPromises.length) {
        await Promise.all(loadPromises);
      }

      this.isEditing = true;
      this.editingId = row.id;

      // Set record info for display
      this.editingRecordInfo = {
        source: row.source,
        enteredBy: row.enteredBy,
        enteredOn: row.enteredOn,
        recordingURL: row.recordingURL || null,
        busChangeRequestID:
          row.busChangeRequestID ||
          row.busChangeRequestId ||
          row.requestID ||
          row.requestId ||
          null,
      };

      await this.loadBusChangeComments(
        this.editingRecordInfo.busChangeRequestID
      );

      // Set selected student info from the row data
      this.selectedStudent = {
        studentID: row.studentID,
        studentName: row.studentName,
        hebName: row.studentNameHeb,
        building: row.building,
        branch: row.branch,
        class: row.class,
        section: row.section,
        address1: row.homeAddress,
        regID: row.term,
        teacherH: row.teacherH,
        teacherH2: row.teacherH2,
      };

      // Fetch full student info to get teacher details
      if (row.studentID) {
        try {
          const studentInfo = await this.$http.get(
            `api/students/getStudent?studentID=${row.studentID}`
          );
          if (studentInfo) {
            this.selectedStudent.teacherH =
              studentInfo.teacherH || row.teacherH;
            this.selectedStudent.teacherH2 =
              studentInfo.teacherH2 || row.teacherH2;
          }
        } catch (e) {
          // Keep the values from the row if API call fails
        }
      }

      // Set form data from the row
      this.newBusChange = {
        startDate: row.startDate ? this.formatDateForPicker(row.startDate) : "",
        endDate: row.endDate ? this.formatDateForPicker(row.endDate) : "",
        startPeriod: row.startPeriod || row.StartPeriod || "",
        endPeriod: row.endPeriod || row.EndPeriod || "",
        busChangeAddress: row.busChangeAddress || "",
        busChangePhoneNumber: row.busChangePhoneNumber || "",
        geoPhoneNumber: row.geoPhoneNumber || "",
        busChangeName: row.busChangeName || "",
        busChangeAccountID: row.busChangeAccountID || "",
        notificationPhoneNumber: row.notificationPhoneNumber || "",
        notificationType: row.notificationType || "",
        note: row.note || "",
      };

      // Set selected pickup and dropoff routes if they exist
      if (row.pickupRouteID) {
        const pickupRoute = this.busRoutes.find(
          (r) => r.id === row.pickupRouteID
        );
        this.selectedPickupRoute = pickupRoute || null;
      }
      if (row.dropoffRouteID) {
        const dropoffRoute = this.busRoutes.find(
          (r) => r.id === row.dropoffRouteID
        );
        this.selectedDropoffRoute = dropoffRoute || null;
      }

      // Track original route IDs for notification detection
      this.originalPickupRouteID = row.pickupRouteID || null;
      this.originalDropoffRouteID = row.dropoffRouteID || null;

      // Track original notification fields for IVR/ONLINE validation
      this.originalNotificationPhone = row.notificationPhoneNumber || null;
      this.originalNotificationType = row.notificationType || null;

      // Load deadlines for deadline warnings
      await this.loadSelectedGradeDeadlines(row.class, row.branch);

      this.addDialog = true;
    },

    formatDateForPicker(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    formatDateTime(dateString) {
      if (!dateString) return "-";
      return moment(dateString).format("MM/DD/YYYY hh:mm A");
    },

    formatPhoneNumber(phoneNumber) {
      if (!phoneNumber) return "";
      // Remove all non-digit characters
      const cleaned = phoneNumber.replace(/\D/g, "");
      // Format as XXX-XXX-XXXX
      if (cleaned.length === 10) {
        return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(
          6
        )}`;
      }
      // Return original if not 10 digits
      return phoneNumber;
    },

    getPeriodOrder(period) {
      if (period === "PM") return 2;
      return 1;
    },

    isEndPeriodBeforeStartPeriod(startPeriod, endPeriod) {
      return this.getPeriodOrder(endPeriod) < this.getPeriodOrder(startPeriod);
    },

    getCommentText(comment) {
      if (!comment) return "";
      return comment.commentText || comment.CommentText || "";
    },

    getCommentEnteredBy(comment) {
      if (!comment) return "";
      return comment.enteredBy || comment.EnteredBy || "";
    },

    getCommentEnteredOn(comment) {
      if (!comment) return null;
      return comment.enteredOn || comment.EnteredOn || null;
    },

    getCommentCallerID(comment) {
      if (!comment) return "";
      return comment.callerID || comment.CallerID || "";
    },

    getCommentRecordingUrl(comment) {
      if (!comment) return null;
      return (
        comment.recordingURL ||
        comment.recordingUrl ||
        comment.RecordingURL ||
        null
      );
    },

    async loadBusChangeComments(busChangeRequestId) {
      this.busChangeComments = [];

      if (!busChangeRequestId) {
        return;
      }

      this.loadingBusChangeComments = true;
      try {
        const response = await this.$http.get(
          `api/BusChange/GetBusChangeComments?busChangeRequestId=${busChangeRequestId}`
        );
        this.busChangeComments = Array.isArray(response) ? response : [];
      } catch (e) {
        this.busChangeComments = [];
      } finally {
        this.loadingBusChangeComments = false;
      }
    },

    onStartDateChange() {
      this.startDateMenu = false;
      this.startDateEdited = true;
    },

    closeDialog() {
      this.addDialog = false;
      this.resetForm();
    },

    resetForm() {
      // Account search
      this.phoneSearch = "";
      this.firstNameSearch = "";
      this.lastNameSearch = "";
      this.houseNumberSearch = "";
      this.streetSearch = "";
      this.accounts = [];
      this.selectedAccount = null;
      this.showNoAccountsMessage = false;

      // Student selection
      this.selectedStudent = null;
      this.students = [];
      this.loadingStudents = false;

      // Other fields
      this.isEditing = false;
      this.editingId = null;
      this.editingRecordInfo = null;
      this.busChangeComments = [];
      this.loadingBusChangeComments = false;
      this.gradeError = null;
      this.startDateEdited = false;
      this.selectedGradeDeadlines = [];
      this.selectedPickupRoute = null;
      this.selectedDropoffRoute = null;
      this.pickupRouteSearch = null;
      this.dropoffRouteSearch = null;
      this.skipRouteWarning = false;
      this.originalPickupRouteID = null;
      this.originalDropoffRouteID = null;
      this.originalNotificationPhone = null;
      this.originalNotificationType = null;
      this.skipNotification = false;
      this.savedBusChangeId = null;
      this.newBusChange = {
        startDate: "",
        endDate: "",
        startPeriod: "",
        endPeriod: "",
        busChangeAddress: "",
        busChangePhoneNumber: "",
        geoPhoneNumber: "",
        busChangeName: "",
        busChangeAccountID: "",
        notificationPhoneNumber: "",
        notificationType: "",
        note: "",
      };
    },

    async loadBusRoutes() {
      this.loadingBusRoutes = true;
      try {
        this.busRoutes = await this.$http.get("api/buschange/GetBusRoutes");
        this.pickupRoutes = this.busRoutes.filter(
          (r) => r.direction === "Pickup"
        );
        this.dropoffRoutes = this.busRoutes.filter(
          (r) => r.direction === "Dropoff"
        );
      } catch (e) {
        this.busRoutes = [];
        this.pickupRoutes = [];
        this.dropoffRoutes = [];
      } finally {
        this.loadingBusRoutes = false;
      }
    },

    onPickupRouteChange(pickupRoute) {
      if (!pickupRoute) {
        return;
      }

      // Helper to check if two comma-separated strings have any overlap
      const hasOverlap = (str1, str2) => {
        if (!str1 || !str2) return false;
        const arr1 = str1.split(",").map((s) => s.trim());
        const arr2 = str2.split(",").map((s) => s.trim());
        return arr1.some((item) => arr2.includes(item));
      };

      // Try to find exact match first (highest priority)
      const exactMatch = this.filteredDropoffRoutes.find((dropoff) => {
        return (
          dropoff.busNumber === pickupRoute.busNumber &&
          dropoff.driver === pickupRoute.driver &&
          dropoff.buildings === pickupRoute.buildings &&
          dropoff.grades === pickupRoute.grades &&
          dropoff.driver_Bus_ID === pickupRoute.driver_Bus_ID
        );
      });

      if (exactMatch) {
        this.selectedDropoffRoute = exactMatch;
        return;
      }

      // Try match with overlapping buildings and grades
      const overlapMatch = this.filteredDropoffRoutes.find((dropoff) => {
        return (
          dropoff.busNumber === pickupRoute.busNumber &&
          dropoff.driver === pickupRoute.driver &&
          hasOverlap(dropoff.buildings, pickupRoute.buildings) &&
          hasOverlap(dropoff.grades, pickupRoute.grades) &&
          dropoff.driver_Bus_ID === pickupRoute.driver_Bus_ID
        );
      });

      if (overlapMatch) {
        this.selectedDropoffRoute = overlapMatch;
        return;
      }

      // Try looser match - just bus number, driver, and overlapping grades
      const looseMatch = this.filteredDropoffRoutes.find((dropoff) => {
        return (
          dropoff.busNumber === pickupRoute.busNumber &&
          dropoff.driver === pickupRoute.driver &&
          hasOverlap(dropoff.grades, pickupRoute.grades)
        );
      });

      if (looseMatch) {
        this.selectedDropoffRoute = looseMatch;
      }
    },

    async loadBranches() {
      try {
        const branchesResponse = await this.$http.get(
          "api/students/getBranchList"
        );
        let branches = Array.isArray(branchesResponse) ? branchesResponse : [];

        // Non-admin users should only see their permitted branches.
        if (!this.isAdmin() && this.permittedBranches.length) {
          const filteredBranches = branches.filter((b) =>
            this.permittedBranches.includes(b)
          );
          branches =
            filteredBranches.length > 0 ? filteredBranches : this.permittedBranches;
        }

        this.branchList = branches;
      } catch (e) {
        this.branchList = [];
      }
    },

    getGradeSetup(grade, branch) {
      if (!grade || !branch || !this.busChangeGrades.length) return null;
      return this.busChangeGrades.find(
        (g) =>
          g.grade &&
          g.grade.trim() === grade.trim() &&
          g.branch &&
          g.branch.trim() === branch.trim()
      );
    },

    checkGradeAllowsBusChange(grade, branch) {
      const setup = this.getGradeSetup(grade, branch);
      if (!setup)
        return {
          allowed: false,
          error: "Bus changes are not allowed for this grade.",
        };

      // Check if bus change is allowed for this grade
      if (!setup.allowBusChange) {
        return {
          allowed: false,
          error: "Bus changes are not allowed for this grade.",
        };
      }

      return { allowed: true, error: null };
    },

    async loadSelectedGradeDeadlines(grade, branch) {
      const setup = this.getGradeSetup(grade, branch);
      if (!setup || !setup.id) {
        this.selectedGradeDeadlines = [];
        return;
      }
      try {
        const response = await this.$http.get(
          "api/busChange/getBusChangeDeadlines?gradeId=" + setup.id
        );
        this.selectedGradeDeadlines = Array.isArray(response) ? response : [];
      } catch (e) {
        this.selectedGradeDeadlines = [];
      }
    },

    checkDeadlineWarning(grade, branch, startDate) {
      if (!startDate || !this.selectedGradeDeadlines.length) return null;

      // Check if start date is today or in the past
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(
        today.getMonth() + 1
      ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

      // startDate is in YYYY-MM-DD format - check if it's today or before
      if (startDate > todayStr) return null;

      // Get day of week for the start date
      const startDateObj = new Date(startDate + "T00:00:00");
      const dayOfWeek = startDateObj.getDay();

      // Check for exception deadline first
      const exception = this.selectedGradeDeadlines.find(
        (d) => d.exceptionDate && d.exceptionDate.startsWith(startDate)
      );

      // Get applicable deadline (exception or default for day of week)
      const deadline =
        exception ||
        this.selectedGradeDeadlines.find(
          (d) => !d.exceptionDate && d.dayOfWeek === dayOfWeek
        );

      if (!deadline || !deadline.deadline) return null;

      // Check if current time is past deadline
      const currentTime =
        today.getHours() * 3600 + today.getMinutes() * 60 + today.getSeconds();

      const deadlineParts = deadline.deadline.split(":");
      if (deadlineParts.length >= 2) {
        const deadlineSeconds =
          parseInt(deadlineParts[0]) * 3600 +
          parseInt(deadlineParts[1]) * 60 +
          (deadlineParts[2] ? parseFloat(deadlineParts[2]) : 0);

        if (currentTime > deadlineSeconds) {
          // Format to 12-hour time for display
          let hours = parseInt(deadlineParts[0]);
          const minutes = deadlineParts[1];
          const ampm = hours >= 12 ? "PM" : "AM";
          hours = hours % 12 || 12;
          const deadlineFormatted = `${hours}:${minutes} ${ampm}`;
          return `Note: The deadline for bus changes has passed (${deadlineFormatted}).`;
        }
      }

      return null;
    },

    async searchByPhone(value) {
      // Clear previous timeout
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      const phoneNumber = value ? value.trim() : "";

      if (!phoneNumber || phoneNumber.length < 10) {
        this.clearAccountSearch();
        return;
      }

      this.showNoAccountsMessage = false;

      // Debounce the search
      this.searchDebounceTimer = setTimeout(async () => {
        try {
          const accounts = await this.$http.get(
            `api/students/getAccountByPhoneNumber?phoneNumber=${phoneNumber}`
          );

          if (accounts.length > 1) {
            this.accounts = accounts;
          } else if (accounts.length === 1) {
            this.selectedAccount = accounts[0];
            await this.loadStudentsByAccount();
          } else {
            this.showNoAccountsMessage = true;
            this.accounts = [];
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
        }
      }, 400);
    },

    async searchByName() {
      // Clear previous timeout
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      const lastName = this.lastNameSearch ? this.lastNameSearch.trim() : "";
      const firstName = this.firstNameSearch ? this.firstNameSearch.trim() : "";

      if (!lastName && !firstName) {
        this.clearAccountSearch();
        return;
      }

      this.showNoAccountsMessage = false;

      // Debounce the search
      this.searchDebounceTimer = setTimeout(async () => {
        try {
          const url = `api/students/getAccountsByName?last=${lastName}&first=${firstName}&isYiddish=${this
            .searchNameMode === "yiddish"}`;

          const accounts = await this.$http.get(url);

          if (!accounts.length) {
            this.showNoAccountsMessage = true;
            this.accounts = [];
            return;
          }

          if (accounts.length === 1) {
            this.selectedAccount = accounts[0];
            await this.loadStudentsByAccount();
          } else {
            this.accounts = accounts;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
        }
      }, 400);
    },

    async searchByAddress() {
      // Clear previous timeout
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      const houseNumber = this.houseNumberSearch
        ? this.houseNumberSearch.trim()
        : "";
      const street = this.streetSearch ? this.streetSearch.trim() : "";

      if (!houseNumber && !street) {
        this.clearAccountSearch();
        return;
      }

      this.showNoAccountsMessage = false;

      // Debounce the search
      this.searchDebounceTimer = setTimeout(async () => {
        try {
          const params = new URLSearchParams();
          if (houseNumber) params.append("houseNumber", houseNumber);
          if (street) params.append("street", street);
          if (this.userBranch) params.append("branch", this.userBranch);

          const url = `api/students/getAccountsByAddress?${params.toString()}`;
          const accounts = await this.$http.get(url);

          if (!accounts.length) {
            this.showNoAccountsMessage = true;
            this.accounts = [];
            return;
          }

          if (accounts.length === 1) {
            this.selectedAccount = accounts[0];
            await this.loadStudentsByAccount();
          } else {
            this.accounts = accounts;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
        }
      }, 400);
    },

    async selectAccount(account) {
      this.selectedAccount = account;
      this.accounts = [];
      await this.loadStudentsByAccount();
    },

    clearAccount() {
      this.selectedAccount = null;
      this.students = [];
      this.selectedStudent = null;
      this.phoneSearch = "";
      this.firstNameSearch = "";
      this.lastNameSearch = "";
      this.houseNumberSearch = "";
      this.streetSearch = "";
      this.showNoAccountsMessage = false;
    },

    clearStudent() {
      this.selectedStudent = null;
      // Keep account and students list - user goes back to student selection
    },

    clearAccountSearch() {
      this.accounts = [];
      this.selectedAccount = null;
      this.students = [];
      this.selectedStudent = null;
      this.showNoAccountsMessage = false;
      this.houseNumberSearch = "";
      this.streetSearch = "";
    },

    async loadStudentsByAccount() {
      if (!this.selectedAccount || !this.selectedAccount.accountID) return;

      this.loadingStudents = true;
      try {
        let students = await this.$http.get(
          `api/students/getStudentsByAccountID?accountID=${
            this.selectedAccount.accountID
          }`
        );

        students = students || [];

        // Filter by branch if userBranch exists
        if (this.userBranch) {
          const allStudentsCount = students.length;
          // Handle comma-separated branches like "2411,2412"
          const allowedBranches = this.userBranch
            .split(",")
            .map((b) => b.trim());
          students = students.filter((s) => allowedBranches.includes(s.branch));

          if (allStudentsCount > 0 && students.length === 0) {
            this.$notify({
              text: `No students found for this account in branch${
                allowedBranches.length > 1 ? "es" : ""
              } ${allowedBranches.join(", ")}`,
              group: "notification",
              type: "warning",
            });
          }
        }

        this.students = students;

        // Auto-select if only one student
        if (this.students.length === 1) {
          await this.selectStudent(this.students[0]);
        } else if (this.students.length === 0 && !this.userBranch) {
          this.$notify({
            text: "No students found for this account",
            group: "notification",
            type: "warning",
          });
        }
      } catch (e) {
        this.$notify({
          text: "Failed to load students",
          group: "notification",
          type: "error",
        });
        this.students = [];
      } finally {
        this.loadingStudents = false;
      }
    },

    async selectStudent(student) {
      // Map student data to expected format
      const mappedStudent = {
        studentID: student.studentID,
        studentName: student.name,
        hebName: student.hebName,
        building: student.building,
        branch: student.branch,
        class: student.class || student.grade, // Use class field, fallback to grade
        section: student.section || "",
        address1: student.address || "",
        regID: student.regID,
        teacherH: student.teacherH || student.teacher,
        teacherH2: student.teacherH2 || student.teacher2,
      };

      // Check if grade allows bus change
      const gradeCheck = this.checkGradeAllowsBusChange(
        mappedStudent.class,
        mappedStudent.branch
      );
      if (!gradeCheck.allowed) {
        this.gradeError = gradeCheck.error;
        this.$notify({
          text: gradeCheck.error,
          group: "notification",
          type: "error",
        });
        return;
      }

      this.gradeError = null;
      this.selectedStudent = mappedStudent;

      // Load deadlines for this student's grade for deadline warnings
      await this.loadSelectedGradeDeadlines(
        mappedStudent.class,
        mappedStudent.branch
      );

      // Pre-fill home address if available
      if (mappedStudent.address1) {
        this.newBusChange.homeAddress = mappedStudent.address1;
      }

      // Pre-fill geo phone number with account home phone
      if (this.selectedAccount && this.selectedAccount.homePhone) {
        this.newBusChange.geoPhoneNumber = this.selectedAccount.homePhone;
      }
    },

    getTeacherDisplay(student) {
      const teachers = [];
      if (student.teacherH) teachers.push(student.teacherH);
      if (student.teacherH2) teachers.push(student.teacherH2);
      return teachers.join(" / ");
    },

    openAccountSearch() {
      // Reset dialog search state
      this.dialogPhoneSearch = "";
      this.dialogFirstNameSearch = "";
      this.dialogLastNameSearch = "";
      this.dialogHouseNumberSearch = "";
      this.dialogStreetSearch = "";
      this.dialogAccounts = [];
      this.dialogShowNoAccountsMessage = false;
      this.accountSearchDialog = true;
    },

    closeAccountSearchDialog() {
      this.accountSearchDialog = false;
      this.dialogPhoneSearch = "";
      this.dialogFirstNameSearch = "";
      this.dialogLastNameSearch = "";
      this.dialogHouseNumberSearch = "";
      this.dialogStreetSearch = "";
      this.dialogAccounts = [];
      this.dialogShowNoAccountsMessage = false;
    },

    async searchDialogByPhone(value) {
      // Clear previous timeout
      if (this.dialogSearchDebounceTimer) {
        clearTimeout(this.dialogSearchDebounceTimer);
      }

      const phoneNumber = value ? value.trim() : "";

      if (!phoneNumber || phoneNumber.length < 10) {
        this.dialogAccounts = [];
        this.dialogShowNoAccountsMessage = false;
        return;
      }

      this.dialogShowNoAccountsMessage = false;

      // Debounce the search
      this.dialogSearchDebounceTimer = setTimeout(async () => {
        try {
          const accounts = await this.$http.get(
            `api/students/getAccountByPhoneNumber?phoneNumber=${phoneNumber}`
          );

          this.dialogAccounts = accounts || [];

          if (this.dialogAccounts.length === 0) {
            this.dialogShowNoAccountsMessage = true;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
          this.dialogAccounts = [];
        }
      }, 400);
    },

    async searchDialogByName() {
      // Clear previous timeout
      if (this.dialogSearchDebounceTimer) {
        clearTimeout(this.dialogSearchDebounceTimer);
      }

      const lastName = this.dialogLastNameSearch
        ? this.dialogLastNameSearch.trim()
        : "";
      const firstName = this.dialogFirstNameSearch
        ? this.dialogFirstNameSearch.trim()
        : "";

      if (!lastName && !firstName) {
        this.dialogAccounts = [];
        this.dialogShowNoAccountsMessage = false;
        return;
      }

      this.dialogShowNoAccountsMessage = false;

      // Debounce the search
      this.dialogSearchDebounceTimer = setTimeout(async () => {
        try {
          const url = `api/students/getAccountsByName?last=${lastName}&first=${firstName}&isYiddish=${this
            .dialogSearchNameMode === "yiddish"}`;

          const accounts = await this.$http.get(url);

          this.dialogAccounts = accounts || [];

          if (this.dialogAccounts.length === 0) {
            this.dialogShowNoAccountsMessage = true;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
          this.dialogAccounts = [];
        }
      }, 400);
    },

    async searchDialogByAddress() {
      // Clear previous timeout
      if (this.dialogSearchDebounceTimer) {
        clearTimeout(this.dialogSearchDebounceTimer);
      }

      const houseNumber = this.dialogHouseNumberSearch
        ? this.dialogHouseNumberSearch.trim()
        : "";
      const street = this.dialogStreetSearch
        ? this.dialogStreetSearch.trim()
        : "";

      if (!houseNumber && !street) {
        this.dialogAccounts = [];
        this.dialogShowNoAccountsMessage = false;
        return;
      }

      this.dialogShowNoAccountsMessage = false;

      // Debounce the search
      this.dialogSearchDebounceTimer = setTimeout(async () => {
        try {
          const params = new URLSearchParams();
          if (houseNumber) params.append("houseNumber", houseNumber);
          if (street) params.append("street", street);
          if (this.userBranch) params.append("branch", this.userBranch);

          const url = `api/students/getAccountsByAddress?${params.toString()}`;
          const accounts = await this.$http.get(url);

          this.dialogAccounts = accounts || [];

          if (this.dialogAccounts.length === 0) {
            this.dialogShowNoAccountsMessage = true;
          }
        } catch (e) {
          this.$notify({
            text: "Failed to search accounts",
            group: "notification",
            type: "error",
          });
          this.dialogAccounts = [];
        }
      }, 400);
    },

    onAccountSelected(account) {
      this.accountSearchDialog = false;
      this.newBusChange.busChangeAccountID = account.accountID;
      // Use name field if available, otherwise combine lastName + firstName
      this.newBusChange.busChangeName =
        account.name ||
        ((account.lastName || "") + " " + (account.firstName || "")).trim();
      if (account.address1 && !this.newBusChange.busChangeAddress) {
        this.newBusChange.busChangeAddress = account.address1;
      }

      // Clear dialog search state
      this.dialogPhoneSearch = "";
      this.dialogFirstNameSearch = "";
      this.dialogLastNameSearch = "";
      this.dialogHouseNumberSearch = "";
      this.dialogStreetSearch = "";
      this.dialogAccounts = [];
      this.dialogShowNoAccountsMessage = false;
    },

    validateForm() {
      // Check required fields
      if (!this.newBusChange.startDate) {
        this.$notify({
          text: "Start Date is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (!this.newBusChange.endDate) {
        this.$notify({
          text: "End Date is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (!this.newBusChange.busChangeAddress) {
        this.$notify({
          text: "Bus Change Address is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (!this.newBusChange.startPeriod) {
        this.$notify({
          text: "Starting Run is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (!this.newBusChange.endPeriod) {
        this.$notify({
          text: "Ending After is required",
          group: "notification",
          type: "error",
        });
        return false;
      }

      // Validate phone numbers if they have values
      const validatePhone = (phoneNumber) => {
        if (!phoneNumber) return true; // Empty is ok (not required)
        const digits = phoneNumber.replace(/[-\s()]/g, "");
        return [10, 11].includes(digits.length) && /^\d+$/.test(digits);
      };

      if (
        this.newBusChange.busChangePhoneNumber &&
        !validatePhone(this.newBusChange.busChangePhoneNumber)
      ) {
        this.$notify({
          text:
            "Bus Change Phone Number is invalid. Please enter a valid 10 or 11 digit phone number.",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (
        this.newBusChange.geoPhoneNumber &&
        !validatePhone(this.newBusChange.geoPhoneNumber)
      ) {
        this.$notify({
          text:
            "Geo Phone Number is invalid. Please enter a valid 10 or 11 digit phone number.",
          group: "notification",
          type: "error",
        });
        return false;
      }

      if (
        this.newBusChange.notificationPhoneNumber &&
        !validatePhone(this.newBusChange.notificationPhoneNumber)
      ) {
        this.$notify({
          text:
            "Notification Phone Number is invalid. Please enter a valid 10 or 11 digit phone number.",
          group: "notification",
          type: "error",
        });
        return false;
      }

      return true;
    },

    async saveBusChange() {
      if (!this.validateForm()) {
        // Validation error already notified in validateForm()
        return;
      }

      // Check grade settings before saving
      const gradeCheck = this.checkGradeAllowsBusChange(
        this.selectedStudent.class,
        this.selectedStudent.branch
      );
      if (!gradeCheck.allowed) {
        this.$notify({
          text: gradeCheck.error,
          group: "notification",
          type: "error",
        });
        return;
      }

      // Validate endDate is not before startDate
      if (this.newBusChange.endDate && this.newBusChange.startDate) {
        if (this.newBusChange.endDate < this.newBusChange.startDate) {
          this.$notify({
            text: "End Date cannot be before Start Date",
            group: "notification",
            type: "error",
          });
          return;
        }

        if (
          this.newBusChange.endDate === this.newBusChange.startDate &&
          this.isEndPeriodBeforeStartPeriod(
            this.newBusChange.startPeriod,
            this.newBusChange.endPeriod
          )
        ) {
          this.$notify({
            text: "Ending After cannot be before Starting Run on the same day.",
            group: "notification",
            type: "error",
          });
          return;
        }
      }

      // Check if notification fields were cleared for IVR/ONLINE sources
      if (this.isEditing && this.isSourceMandatory && !this.skipNotification) {
        const hadNotificationInfo =
          this.originalNotificationPhone || this.originalNotificationType;
        const currentHasNotificationInfo =
          this.newBusChange.notificationPhoneNumber &&
          this.newBusChange.notificationType;

        if (hadNotificationInfo && !currentHasNotificationInfo) {
          this.clearedNotificationWarningDialog = true;
          return;
        }
      }

      // Check if only one route is selected
      if (!this.skipRouteWarning) {
        const hasPickup =
          this.selectedPickupRoute && this.selectedPickupRoute.id;
        const hasDropoff =
          this.selectedDropoffRoute && this.selectedDropoffRoute.id;

        if ((hasPickup && !hasDropoff) || (!hasPickup && hasDropoff)) {
          this.incompleteRouteDialog = true;
          return;
        }
      }

      // Check if notification should be sent (routes changed and both assigned)
      if (!this.skipNotification) {
        const currentPickupID = this.selectedPickupRoute
          ? this.selectedPickupRoute.id
          : null;
        const currentDropoffID = this.selectedDropoffRoute
          ? this.selectedDropoffRoute.id
          : null;

        // Check if routes were changed or assigned for the first time
        const routesChanged =
          currentPickupID !== this.originalPickupRouteID ||
          currentDropoffID !== this.originalDropoffRouteID;

        // Both routes must be assigned to send notification
        const bothRoutesAssigned = currentPickupID && currentDropoffID;

        if (routesChanged && bothRoutesAssigned) {
          // Check if notification fields are filled
          const hasNotificationPhone = this.newBusChange
            .notificationPhoneNumber;
          const hasNotificationType = this.newBusChange.notificationType;

          if (!hasNotificationPhone || !hasNotificationType) {
            // Show missing fields dialog
            this.missingNotificationFieldsDialog = true;
            return;
          } else {
            // Show notification confirmation dialog
            this.notificationConfirmDialog = true;
            return;
          }
        }
      }

      this.saving = true;
      try {
        const data = {
          studentID: this.selectedStudent.studentID,
          startDate: this.newBusChange.startDate
            ? moment(this.newBusChange.startDate).format("MM/DD/YYYY")
            : null,
          endDate: this.newBusChange.endDate
            ? moment(this.newBusChange.endDate).format("MM/DD/YYYY")
            : null,
          startPeriod: this.newBusChange.startPeriod,
          endPeriod: this.newBusChange.endPeriod,
          homeAddress: this.selectedStudent.address1 || "",
          busChangeAddress: this.newBusChange.busChangeAddress,
          busChangePhoneNumber: this.newBusChange.busChangePhoneNumber,
          geoPhoneNumber: this.newBusChange.geoPhoneNumber,
          busChangeName: this.newBusChange.busChangeName,
          busChangeAccountID: this.newBusChange.busChangeAccountID,
          notificationPhoneNumber: this.newBusChange.notificationPhoneNumber,
          notificationType: this.newBusChange.notificationType,
          pickupRouteID: this.selectedPickupRoute
            ? this.selectedPickupRoute.id
            : null,
          dropoffRouteID: this.selectedDropoffRoute
            ? this.selectedDropoffRoute.id
            : null,
          note: this.newBusChange.note,
          building: this.selectedStudent.building,
          branch: this.selectedStudent.branch,
          class: this.selectedStudent.class,
          section: this.selectedStudent.section,
          term: this.selectedStudent.regID,
          enteredBy: this.$store.state.authentication.user,
          source: "ADMIN PORTAL",
        };

        // Include ID when editing
        if (this.isEditing && this.editingId) {
          data.id = this.editingId;
        }

        const response = await this.$http.post("api/buschange/save", data);

        if (response.error) {
          return this.$notify({
            group: "notification",
            type: "error",
            text: response.error,
          });
        }

        // Get the bus change ID from response
        const busChangeId = response.id || this.editingId;

        this.$notify({
          text: this.isEditing
            ? "Bus change updated successfully"
            : "Bus change saved successfully",
          group: "notification",
          type: "success",
        });

        // Send notification if needed
        if (this.savedBusChangeId && busChangeId) {
          await this.sendBusChangeNotification(busChangeId);
        }

        this.closeDialog();
        this.$refs.grid.reloadGrid();
      } catch (e) {
        this.$notify({
          text: "Failed to save bus change",
          group: "notification",
          type: "error",
        });
      } finally {
        this.saving = false;
        this.skipRouteWarning = false;
        this.skipNotification = false;
        this.savedBusChangeId = null;
      }
    },

    confirmSaveWithIncompleteRoute() {
      this.incompleteRouteDialog = false;
      this.skipRouteWarning = true;
      this.saveBusChange();
    },

    // Notification dialog handlers
    proceedWithNotification() {
      this.notificationConfirmDialog = false;
      this.skipNotification = true;
      this.savedBusChangeId = true; // Flag to send notification after save
      this.saveBusChange();
    },

    proceedWithoutNotification() {
      this.notificationConfirmDialog = false;
      this.skipNotification = true;
      this.savedBusChangeId = null; // Don't send notification
      this.saveBusChange();
    },

    cancelSave() {
      this.notificationConfirmDialog = false;
      this.missingNotificationFieldsDialog = false;
      this.clearedNotificationWarningDialog = false;
      this.skipNotification = false;
      this.savedBusChangeId = null;
    },

    saveWithoutNotification() {
      this.missingNotificationFieldsDialog = false;
      this.skipNotification = true;
      this.savedBusChangeId = null; // Don't send notification
      this.saveBusChange();
    },

    closeMissingFieldsDialog() {
      this.missingNotificationFieldsDialog = false;
      this.skipNotification = false;
      this.savedBusChangeId = null;
      // User can now fill in the fields and try saving again
    },

    cancelClearedNotificationWarning() {
      this.clearedNotificationWarningDialog = false;
      this.skipNotification = false;
      // User stays in edit mode to add notification fields back
    },

    confirmSaveWithoutNotification() {
      this.clearedNotificationWarningDialog = false;
      this.skipNotification = true;
      this.savedBusChangeId = null; // Don't send notification
      this.saveBusChange();
    },

    async sendBusChangeNotification(busChangeId) {
      this.sendingNotification = true;
      try {
        const apiUrl = `https://broadcast-3t6ck5zrrq-ue.a.run.app/busChangeNotification/sendBusChangeNotification?busChangeId=${busChangeId}`;
        const response = await this.$http.post(apiUrl, {});

        if (response && !response.error) {
          this.$notify({
            text: "Bus change notification sent successfully",
            group: "notification",
            type: "success",
          });
        } else {
          this.$notify({
            text: `Notification could not be sent: ${response.error ||
              "Unknown error"}`,
            group: "notification",
            type: "warning",
          });
        }
      } catch (error) {
        this.$notify({
          text:
            "Failed to send bus change notification. The bus change was saved but notification failed.",
          group: "notification",
          type: "warning",
        });
      } finally {
        this.sendingNotification = false;
      }
    },

    // Delete methods
    openDeleteDialog() {
      this.recordToDelete = {
        id: this.editingId,
        studentName: this.selectedStudent ? this.selectedStudent.hebName : "",
      };
      this.deleteDialog = true;
    },

    async confirmDelete() {
      if (!this.recordToDelete || !this.recordToDelete.id) return;

      this.deleting = true;
      try {
        await this.$http.post("api/busChange/delete", [this.recordToDelete.id]);

        this.$notify({
          text: "Bus change deleted successfully",
          group: "notification",
          type: "success",
        });

        this.deleteDialog = false;
        this.closeDialog();
        this.$refs.grid.reloadGrid();
      } catch (e) {
        this.$notify({
          text: "Failed to delete bus change",
          group: "notification",
          type: "error",
        });
      } finally {
        this.deleting = false;
      }
    },

    // Grades Setup methods
    async openGradesSetupDialog() {
      this.gradesSetupDialog = true;
      this.editingGrade = false;
      this.selectedGrades = [];
      this.loadingGradesSetup = true;
      await Promise.all([this.loadBusChangeGrades(), this.loadBranches()]);
      this.loadingGradesSetup = false;
    },

    closeGradesSetupDialog() {
      this.gradesSetupDialog = false;
      this.editingGrade = false;
      this.selectedGrades = [];
      this.resetCurrentGrade();
    },

    async loadBusChangeGrades() {
      try {
        const response = await this.$http.get(
          "api/busChange/getBusChangeGrades"
        );
        this.busChangeGrades = Array.isArray(response) ? response : [];
      } catch (e) {
        this.busChangeGrades = [];
      }
    },

    async openAddGrade() {
      this.resetCurrentGrade();
      if (!this.isAdmin() && this.permittedBranches.length === 1) {
        // Only auto-select when there is a single permitted branch.
        const onlyBranch = this.permittedBranches[0];
        this.currentGrade.branch = onlyBranch;
        await this.loadAvailableGrades(onlyBranch);
      }
      this.editingGrade = true;
    },

    async onGradeBranchChange() {
      this.currentGrade.grade = "";
      if (this.currentGrade.branch) {
        await this.loadAvailableGrades(this.currentGrade.branch);
      } else {
        this.availableGrades = [];
      }
    },

    async loadAvailableGrades(branch) {
      this.loadingAvailableGrades = true;
      try {
        const response = await this.$http.get(
          "api/students/getGradeList?branch=" + branch
        );
        this.availableGrades = Array.isArray(response) ? response : [];
      } catch (e) {
        this.availableGrades = [];
      } finally {
        this.loadingAvailableGrades = false;
      }
    },

    async openEditGrade(grade) {
      this.currentGrade = {
        id: grade.id,
        grade: grade.grade,
        branch: grade.branch,
        allowBusChange: grade.allowBusChange,
      };
      this.editingGrade = true;
      await Promise.all([
        this.loadAvailableGrades(grade.branch),
        this.loadGradeDeadlines(grade.id),
      ]);
    },

    async loadGradeDeadlines(gradeId) {
      try {
        const response = await this.$http.get(
          "api/busChange/getBusChangeDeadlines?gradeId=" + gradeId
        );
        const deadlines = Array.isArray(response) ? response : [];
        this.currentDeadlines = deadlines.filter((d) => !d.exceptionDate);
        this.currentExceptions = deadlines.filter((d) => d.exceptionDate);
      } catch (e) {
        this.currentDeadlines = [];
        this.currentExceptions = [];
      }
    },

    resetCurrentGrade() {
      this.currentGrade = {
        id: null,
        grade: "",
        branch: "",
        allowBusChange: true,
      };
      this.currentDeadlines = [];
      this.currentExceptions = [];
      this.availableGrades = [];
      this.multipleGrades = [];
      this.showUnsavedWarning = false;
    },

    backToGradesList() {
      this.editingGrade = false;
      this.resetCurrentGrade();
    },

    formatTimeDisplay(timeStr) {
      if (!timeStr) return "";
      const parts = timeStr.split(":");
      if (parts.length < 2) return "";
      let hours = parseInt(parts[0]);
      const minutes = parts[1];
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      return hours + ":" + minutes + " " + ampm;
    },

    parseTimeForPicker(timeStr) {
      if (!timeStr) return null;
      const parts = timeStr.split(":");
      if (parts.length < 2) return null;
      return parts[0].padStart(2, "0") + ":" + parts[1];
    },

    getDefaultDeadline(dayOfWeek) {
      const deadline = this.currentDeadlines.find(
        (d) => d.dayOfWeek === dayOfWeek
      );
      return deadline ? deadline.deadline : null;
    },

    setDefaultDeadline(dayOfWeek, timeValue) {
      const existing = this.currentDeadlines.find(
        (d) => d.dayOfWeek === dayOfWeek
      );
      if (existing) {
        existing.deadline = timeValue + ":00";
        existing._changed = true;
      } else {
        this.currentDeadlines.push({
          id: null,
          gradeID: this.currentGrade.id,
          dayOfWeek: dayOfWeek,
          type: "Default",
          exceptionDate: null,
          deadline: timeValue + ":00",
          _isNew: true,
        });
      }
    },

    getDefaultDeadlineAsDate(dayOfWeek) {
      const timeStr = this.getDefaultDeadline(dayOfWeek);
      if (!timeStr) return null;
      const parts = timeStr.split(":");
      if (parts.length < 2) return null;
      const date = new Date();
      date.setHours(parseInt(parts[0]), parseInt(parts[1]), 0, 0);
      return date;
    },

    setDefaultDeadlineFromDate(dayOfWeek, dateValue) {
      if (!dateValue) return;
      const hours = String(dateValue.getHours()).padStart(2, "0");
      const minutes = String(dateValue.getMinutes()).padStart(2, "0");
      this.setDefaultDeadline(dayOfWeek, `${hours}:${minutes}`);
    },

    getExceptionDeadlineAsDate() {
      const timeStr = this.newException.deadline;
      if (!timeStr) return null;
      const parts = timeStr.split(":");
      if (parts.length < 2) return null;
      const date = new Date();
      date.setHours(parseInt(parts[0]), parseInt(parts[1]), 0, 0);
      return date;
    },

    setExceptionDeadlineFromDate(e) {
      if (!e.value) return;
      const hours = String(e.value.getHours()).padStart(2, "0");
      const minutes = String(e.value.getMinutes()).padStart(2, "0");
      this.newException.deadline = `${hours}:${minutes}:00`;
    },

    getDaysWithDeadlines(excludeDay) {
      return this.weekDays.filter(
        (d) => d.value !== excludeDay && this.getDefaultDeadline(d.value)
      );
    },

    copyDeadlineFromDay(fromDay, toDay) {
      const sourceDeadline = this.getDefaultDeadline(fromDay);
      if (sourceDeadline) {
        const timeValue = this.parseTimeForPicker(sourceDeadline);
        this.setDefaultDeadline(toDay, timeValue);
      }
    },

    addException() {
      this.editingException = null;
      this.newException = { exceptionDate: "", deadline: "" };
      this.exceptionDialog = true;
    },

    editException(exception) {
      this.editingException = exception;
      this.newException = {
        exceptionDate: this.formatDateForPicker(exception.exceptionDate),
        deadline: exception.deadline,
      };
      this.exceptionDialog = true;
    },

    saveException() {
      if (!this.newException.exceptionDate || !this.newException.deadline) {
        this.$notify({
          text: "Please fill in date and time",
          group: "notification",
          type: "error",
        });
        return;
      }

      if (this.editingException) {
        this.editingException.exceptionDate = this.newException.exceptionDate;
        this.editingException.deadline = this.newException.deadline;
        this.editingException._changed = true;
      } else {
        this.currentExceptions.push({
          id: null,
          gradeID: this.currentGrade.id,
          dayOfWeek: new Date(this.newException.exceptionDate).getDay(),
          type: "Exception",
          exceptionDate: this.newException.exceptionDate,
          deadline: this.newException.deadline,
          _isNew: true,
        });
      }
      this.showUnsavedWarning = true;
      this.exceptionDialog = false;
    },

    removeException(exception) {
      if (exception.id) {
        exception._deleted = true;
      } else {
        const index = this.currentExceptions.indexOf(exception);
        if (index > -1) {
          this.currentExceptions.splice(index, 1);
        }
      }

      this.showUnsavedWarning = true;
      this.$forceUpdate();
    },

    formatExceptionDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },

    formatExceptionDateShort(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    },

    confirmDeleteGrade(grade) {
      this.gradeToDelete = grade;
      this.deleteGradeDialog = true;
    },

    async deleteGrade() {
      this.deletingGrade = true;
      try {
        await this.$http.post(
          "api/busChange/deleteBusChangeGrade?id=" + this.gradeToDelete.id
        );
        this.$notify({
          text: "Grade deleted successfully",
          group: "notification",
          type: "success",
        });
        this.deleteGradeDialog = false;
        await this.loadBusChangeGrades();
      } catch (e) {
        this.$notify({
          text: e.message || "Failed to delete grade",
          group: "notification",
          type: "error",
        });
      } finally {
        this.deletingGrade = false;
      }
    },

    async saveCurrentGrade() {
      // Validation
      if (!this.currentGrade.branch) {
        this.$notify({
          text: "Please select a branch",
          group: "notification",
          type: "error",
        });
        return;
      }

      // For new grades, check multipleGrades array
      const isNewGrade = !this.currentGrade.id;
      if (isNewGrade && this.multipleGrades.length === 0) {
        this.$notify({
          text: "Please select at least one grade",
          group: "notification",
          type: "error",
        });
        return;
      }

      // For editing, check single grade
      if (!isNewGrade && !this.currentGrade.grade) {
        this.$notify({
          text: "Please select a grade",
          group: "notification",
          type: "error",
        });
        return;
      }

      this.savingGradesSetup = true;

      // Get list of grades to save
      const gradesToSave = isNewGrade
        ? this.multipleGrades
        : [this.currentGrade.grade];

      const successGrades = [];
      const failedGrades = [];

      for (const gradeName of gradesToSave) {
        try {
          // Save grade
          const gradeResponse = await this.$http.post(
            "api/busChange/saveBusChangeGrade",
            {
              id: isNewGrade ? 0 : this.currentGrade.id,
              grade: gradeName,
              branch: this.currentGrade.branch,
              allowBusChange: this.currentGrade.allowBusChange,
              createdBy: this.$store.state.authentication.user,
            }
          );

          const gradeId = gradeResponse.id || this.currentGrade.id;

          // Save/update deadlines for each grade
          const allDeadlines = [
            ...this.currentDeadlines,
            ...this.currentExceptions,
          ];
          for (const deadline of allDeadlines) {
            if (deadline._deleted && deadline.id) {
              await this.$http.post(
                "api/busChange/deleteBusChangeDeadline?id=" + deadline.id
              );
            } else if (deadline._isNew || deadline._changed) {
              await this.$http.post("api/busChange/saveBusChangeDeadline", {
                id: isNewGrade ? 0 : deadline.id || 0,
                gradeID: gradeId,
                dayOfWeek: deadline.dayOfWeek,
                type: deadline.type,
                exceptionDate: deadline.exceptionDate,
                deadline: deadline.deadline,
                createdBy: this.$store.state.authentication.user,
              });
            }
          }

          successGrades.push(gradeName);
        } catch (e) {
          failedGrades.push(gradeName);
        }
      }

      // Show appropriate notification
      if (gradesToSave.length === 1) {
        // Single grade - simple message
        if (successGrades.length === 1) {
          this.$notify({
            text: "Grade saved successfully",
            group: "notification",
            type: "success",
          });
        } else {
          this.$notify({
            text: "Failed to save grade",
            group: "notification",
            type: "error",
          });
        }
      } else {
        // Multiple grades - show summary
        if (failedGrades.length === 0) {
          this.$notify({
            text: `All ${successGrades.length} grades saved successfully`,
            group: "notification",
            type: "success",
          });
        } else if (successGrades.length === 0) {
          this.$notify({
            text: `Failed to save all ${failedGrades.length} grades`,
            group: "notification",
            type: "error",
          });
        } else {
          this.$notify({
            text: `Saved: ${successGrades.join(", ")}`,
            group: "notification",
            type: "success",
          });
          this.$notify({
            text: `Failed: ${failedGrades.join(", ")}`,
            group: "notification",
            type: "error",
          });
        }
      }

      await this.loadBusChangeGrades();

      // Only go back if at least one succeeded or if single grade
      if (successGrades.length > 0 || gradesToSave.length === 1) {
        this.backToGradesList();
      }

      this.savingGradesSetup = false;
    },

    // Bulk Exception Methods
    openBulkExceptionDialog() {
      if (this.selectedGrades.length === 0) {
        this.$notify({
          text: "Please select at least one grade",
          group: "notification",
          type: "error",
        });
        return;
      }
      this.bulkException = { exceptionDate: "", deadline: "" };
      this.bulkExceptionDialog = true;
    },

    getSelectedGradeDetails() {
      return this.busChangeGrades.filter((g) =>
        this.selectedGrades.includes(g.id)
      );
    },

    getBulkExceptionDeadlineAsDate() {
      const timeStr = this.bulkException.deadline;
      if (!timeStr) return null;
      const parts = timeStr.split(":");
      if (parts.length < 2) return null;
      const date = new Date();
      date.setHours(parseInt(parts[0]), parseInt(parts[1]), 0, 0);
      return date;
    },

    setBulkExceptionDeadlineFromDate(e) {
      if (!e.value) return;
      const hours = String(e.value.getHours()).padStart(2, "0");
      const minutes = String(e.value.getMinutes()).padStart(2, "0");
      this.bulkException.deadline = `${hours}:${minutes}:00`;
    },

    async saveBulkException() {
      // Validation
      if (!this.bulkException.exceptionDate || !this.bulkException.deadline) {
        this.$notify({
          text: "Please fill in date and time",
          group: "notification",
          type: "error",
        });
        return;
      }

      this.savingBulkException = true;
      try {
        // Convert deadline to TimeSpan format (HH:mm:ss)
        const deadlineTimeSpan = this.bulkException.deadline;

        const response = await this.$http.post(
          "api/busChange/BulkAddExceptions",
          {
            gradeIDs: this.selectedGrades,
            exceptionDate: this.bulkException.exceptionDate,
            deadline: deadlineTimeSpan,
            createdBy: this.$store.state.authentication.user,
          }
        );

        this.$notify({
          text: `Exception added to ${
            this.selectedGrades.length
          } grade(s) successfully`,
          group: "notification",
          type: "success",
        });

        this.bulkExceptionDialog = false;
        this.selectedGrades = [];
        await this.loadBusChangeGrades();
      } catch (e) {
        // this.$notify({
        //   text: e.message || e.Message || "Failed to add exception",
        //   group: "notification",
        //   type: "error",
        // });
      } finally {
        this.savingBulkException = false;
      }
    },

    // Report Download Methods
    async openReportDialog() {
      this.reportDialog = true;
      this.reportType = "standard";
      const today = moment().format("YYYY-MM-DD");
      this.reportFilterStartDate = today;
      this.reportFilterEndDate = today;
      this.showCompletedBusChanges = false;

      // Load filter options and bus changes
      await Promise.all([
        this.loadReportBranches(),
        this.loadReportBusNumbers(),
        this.loadReportBusChanges(),
      ]);

      // Pre-fill branch if user has only one branch
      if (this.userBranch && !this.userBranch.includes(",")) {
        this.reportFilterBranch = this.userBranch;
      }

      // Load classes after branch is set
      if (this.reportFilterBranch) {
        await this.loadReportClasses();
      }

      // Apply initial filters
      this.applyReportFilters();
    },

    closeReportDialog() {
      this.reportDialog = false;
      this.reportFilterBranch = null;
      this.reportFilterClass = [];
      this.reportFilterBusNumber = [];
      this.reportFilterStartDate = null;
      this.reportFilterEndDate = null;
      this.showCompletedBusChanges = false; // Reset to hide completed
      this.reportClasses = [];
      this.reportBusChanges = [];
      this.reportBusChangesFullData = [];
      this.filteredReportBusChanges = [];
      this.selectedReportBusChanges = [];
      this.reportPagination.page = 1;
    },

    async loadReportBranches() {
      if (this.reportBranches.length > 0) return;

      try {
        this.loadingReportOptions = true;
        const response = await this.$http.get("odata/branches");
        let branches = response.value
          .filter((b) => !b.inactive)
          .map((b) => b.branchCode);

        // Filter branches based on user's branch access
        if (this.userBranch) {
          const allowedBranches = this.userBranch
            .split(",")
            .map((b) => b.trim());
          branches = branches.filter((b) => allowedBranches.includes(b));
        }

        this.reportBranches = branches;
      } catch (error) {
        console.error("Error loading branches:", error);
        this.$notify({
          type: "error",
          text: "Failed to load branches",
        });
      } finally {
        this.loadingReportOptions = false;
      }
    },

    async loadReportClasses() {
      if (!this.reportFilterBranch) return;
      if (this.reportClasses.length > 0) return;

      try {
        this.loadingReportOptions = true;
        const grades = await this.$http.get(
          `api/students/getGradeList?branch=${this.reportFilterBranch}`
        );
        this.reportClasses = grades.sort();
      } catch (error) {
        console.error("Error loading classes:", error);
        this.$notify({
          type: "error",
          text: "Failed to load classes",
        });
      } finally {
        this.loadingReportOptions = false;
      }
    },

    async loadReportBusChanges() {
      try {
        this.loadingReportBusChanges = true;

        // Load bus routes if not already loaded
        if (!this.busRoutes || this.busRoutes.length === 0) {
          await this.loadBusRoutes();
        }

        // Load bus changes (including completed ones for report)
        const url =
          "odata/busChange?" +
          (this.userBranch ? "branch=" + this.userBranch : "branch=") +
          "&includeCompleted=true";

        const response = await this.$http.get(url);

        // Store the full original odata response
        this.reportBusChangesFullData = response.value;

        // Transform the data to include the fields we need for display
        this.reportBusChanges = response.value.map((bc) => {
          // Find the pickup or dropoff route to get bus number
          let busNumber = "";
          if (bc.pickupRouteID) {
            const pickupRoute = this.busRoutes.find(
              (r) => r.id === bc.pickupRouteID
            );
            if (pickupRoute) {
              busNumber = pickupRoute.busNumber || "";
            }
          }
          // If no bus number from pickup, try dropoff
          if (!busNumber && bc.dropoffRouteID) {
            const dropoffRoute = this.busRoutes.find(
              (r) => r.id === bc.dropoffRouteID
            );
            if (dropoffRoute) {
              busNumber = dropoffRoute.busNumber || "";
            }
          }

          return {
            id: bc.id,
            studentID: bc.studentID,
            studentName: bc.studentName || "",
            studentNameHeb: bc.studentNameHeb || "",
            branch: bc.branch || "",
            class: bc.class || "",
            busNumber: busNumber,
            startDate: bc.startDate,
            endDate: bc.endDate,
            familyID: bc.familyID,
            status: bc.status,
            busChangeName: bc.busChangeName || "",
            busChangeAddress: bc.busChangeAddress || "",
            pickupRouteID: bc.pickupRouteID,
            dropoffRouteID: bc.dropoffRouteID,
          };
        });

        this.applyReportFilters();
      } catch (error) {
        console.error("Error loading bus changes:", error);
        this.$notify({
          type: "error",
          text: "Failed to load bus changes",
        });
      } finally {
        this.loadingReportBusChanges = false;
      }
    },

    applyReportFilters() {
      let filtered = [...this.reportBusChanges];

      // Filter by completed status
      if (!this.showCompletedBusChanges) {
        filtered = filtered.filter(
          (bc) => bc.status && bc.status.trim().toUpperCase() !== "COMPLETED"
        );
      }

      // Filter by branch
      if (this.reportFilterBranch) {
        filtered = filtered.filter(
          (bc) => bc.branch === this.reportFilterBranch
        );
      }

      // Filter by class
      if (this.reportFilterClass && this.reportFilterClass.length > 0) {
        filtered = filtered.filter((bc) =>
          this.reportFilterClass.includes(bc.class)
        );
      }

      // Filter by bus number
      if (this.reportFilterBusNumber && this.reportFilterBusNumber.length > 0) {
        filtered = filtered.filter((bc) =>
          this.reportFilterBusNumber.includes(bc.busNumber)
        );
      }

      // Filter by selected date range (show bus changes active during the range)
      if (this.reportFilterStartDate && this.reportFilterEndDate) {
        filtered = filtered.filter((bc) => {
          const busChangeStart = this.formatDateForPicker(bc.startDate);
          const busChangeEnd = bc.endDate
            ? this.formatDateForPicker(bc.endDate)
            : "9999-12-31";
          return (
            busChangeStart <= this.reportFilterEndDate &&
            busChangeEnd >= this.reportFilterStartDate
          );
        });
      } else if (this.reportFilterStartDate) {
        filtered = filtered.filter((bc) => {
          const busChangeEnd = bc.endDate
            ? this.formatDateForPicker(bc.endDate)
            : "9999-12-31";
          return busChangeEnd >= this.reportFilterStartDate;
        });
      } else if (this.reportFilterEndDate) {
        filtered = filtered.filter((bc) => {
          const busChangeStart = this.formatDateForPicker(bc.startDate);
          return busChangeStart <= this.reportFilterEndDate;
        });
      }

      this.filteredReportBusChanges = filtered;

      // Reset to first page when filters change
      this.reportPagination.page = 1;
    },

    clearStartDateFilter() {
      this.reportFilterStartDate = null;
      this.$nextTick(() => {
        this.applyReportFilters();
      });
    },

    clearEndDateFilter() {
      this.reportFilterEndDate = null;
      this.$nextTick(() => {
        this.applyReportFilters();
      });
    },

    selectAllReportBusChanges() {
      this.selectedReportBusChanges = [...this.filteredReportBusChanges];
    },

    clearAllReportBusChanges() {
      this.selectedReportBusChanges = [];
    },

    formatDate(date) {
      if (!date) return "";
      return moment(date).format("MM/DD/YYYY");
    },

    getStatusIcon(status) {
      if (!status) return "";
      switch (status.trim().toUpperCase()) {
        case "NEW":
          return "mdi-new-box";
        case "FUTURE":
        case "PENDING":
          return "mdi-clock-outline";
        case "ACTIVE":
          return "mdi-check-circle";
        case "COMPLETED":
          return "mdi-check-all";
        default:
          return "mdi-information";
      }
    },

    getStatusIconColor(status) {
      if (!status) return "";
      switch (status.trim().toUpperCase()) {
        case "NEW":
          return "#2196f3";
        case "FUTURE":
        case "PENDING":
          return "#ff9800";
        case "ACTIVE":
          return "rgb(92, 184, 96)";
        case "COMPLETED":
          return "#9e9e9e";
        default:
          return "#757575";
      }
    },

    async loadReportBusNumbers() {
      if (this.reportBusNumbers.length > 0) return;

      try {
        this.loadingReportOptions = true;

        // Use existing loadBusRoutes if routes not already loaded
        if (!this.busRoutes.length) {
          await this.loadBusRoutes();
        }

        // Get unique bus numbers from loaded routes
        this.reportBusNumbers = [
          ...new Set(this.busRoutes.map((r) => r.busNumber)),
        ]
          .filter((n) => n)
          .sort((a, b) => {
            const numA = parseInt(a);
            const numB = parseInt(b);
            if (!isNaN(numA) && !isNaN(numB)) {
              return numA - numB;
            }
            return a.localeCompare(b);
          });
      } catch (error) {
        console.error("Error loading bus numbers:", error);
        this.$notify({
          type: "error",
          text: "Failed to load bus numbers",
        });
      } finally {
        this.loadingReportOptions = false;
      }
    },

    async downloadReport(reportType) {
      if (this.selectedReportBusChanges.length === 0) {
        this.$notify({
          type: "error",
          text: "Please select at least one bus change",
        });
        return;
      }

      try {
        this.downloadingReport = reportType;

        // Determine API endpoint based on report type
        const apiUrl =
          reportType === "detailed"
            ? "api/buschange/GenerateBusChangesDetailedPdf"
            : "api/buschange/GenerateBusChangesPdf";

        // Map selected items back to their full original odata response
        // Maintain the order from sortedReportBusChanges (current grid sort order)
        const fullBusChangesData = this.sortedReportBusChanges
          .filter((displayItem) =>
            this.selectedReportBusChanges.some(
              (selected) => selected.id === displayItem.id
            )
          )
          .map((displayItem) => {
            // Find the full data for this item
            return this.reportBusChangesFullData.find(
              (fullData) => fullData.id === displayItem.id
            );
          })
          .filter((item) => item); // Remove any undefined items

        // Send full bus changes data as POST data (in user's current sort order)
        const response = await this.$http.post(apiUrl, fullBusChangesData, {
          responseType: "blob",
        });

        // Create download link
        const blob = new Blob([response], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);

        const reportTypeName =
          reportType === "detailed" ? "Detailed" : "Standard";
        link.download = `BusChanges_${reportTypeName}_${moment().format(
          "YYYYMMDD_HHmmss"
        )}.pdf`;
        link.click();
        window.URL.revokeObjectURL(link.href);

        this.$notify({
          type: "success",
          text: `${reportTypeName} report downloaded successfully`,
        });

        this.closeReportDialog();
      } catch (error) {
        // Error is handled by axios interceptor
        console.error("Error downloading report:", error);
      } finally {
        this.downloadingReport = null;
      }
    },
  },
};
</script>

<style scoped>
.bus-change-dialog .dialog-header {
  background: linear-gradient(135deg, rgb(92, 184, 96) 0%, #388e3c 100%);
  color: white;
  padding: 0px 16px;
}

.bus-change-dialog .dialog-title {
  font-size: 20px;
  font-weight: 400;
}

.bus-change-dialog .section-header {
  font-weight: 600;
  color: rgb(92, 184, 96);
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  padding-bottom: 8px;
  border-bottom: 2px solid #e8f5e9;
}

.student-list-container {
  max-height: 230px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
}

.student-list {
  background: transparent;
}

.student-item {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #eeeeee;
}

.student-item:hover {
  background-color: #e8f5e9 !important;
}

.student-item:last-child {
  border-bottom: none;
}

.selected-student-card {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border: 2px solid rgb(92, 184, 96) !important;
  border-radius: 12px;
  padding: 8px;
}

.record-info-with-audio {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.record-info-compact {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: 11px;
  color: #757575;
  padding: 4px 8px;
  background: #fafafa;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  flex: 1;
  min-width: 300px;
}

.record-info-compact .info-item {
  display: inline-flex;
  align-items: center;
}

.record-info-compact .info-divider {
  margin: 0 8px;
  color: #bdbdbd;
}

.recording-inline {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 8px 12px;
  background: linear-gradient(135deg, #fff5f5 0%, #fff 100%);
  border-radius: 8px;
  border: 1px solid #ffcccc;
  box-shadow: 0 1px 3px rgba(255, 107, 107, 0.1);
  min-width: 280px;
}

.recording-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.recording-icon {
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

.recording-label {
  font-size: 11px;
  font-weight: 600;
  color: #d63031;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.recording-player {
  width: 100%;
  max-width: 280px;
  height: 32px;
  border-radius: 4px;
}

.comments-card {
  background: #fafafa;
  max-height: 460px;
  overflow-y: auto;
}

.compact-info {
  background: #fafafa;
}

.info-row {
  display: flex;
  padding: 6px 0;
  border-bottom: 1px solid #f1f1f1;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 12px;
  color: #757575;
  min-width: 56px;
  flex-shrink: 0;
}

.info-value {
  font-size: 12px;
  color: #212121;
  font-weight: 500;
}

.comment-entry {
  border: 1px solid #ececec;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 8px;
  background: white;
}

.comment-entry:last-child {
  margin-bottom: 0;
}

.comment-meta {
  font-size: 12px;
  color: #616161;
  margin-bottom: 4px;
}

.comment-caller {
  font-size: 12px;
  margin-bottom: 4px;
}

.comment-text {
  font-size: 13px;
  white-space: pre-wrap;
}

.comment-audio {
  width: 100%;
  min-height: 36px;
}

.period-helper-text {
  line-height: 1.35;
  padding-left: 2px;
}

.period-section-card {
  background: #fcfcfc;
  border-color: #e9e9e9 !important;
}

.period-title {
  color: #5f5f5f;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.flex-grow-1 {
  flex-grow: 1;
}

.dialog-header {
  background: linear-gradient(135deg, rgb(92, 184, 96) 0%, #388e3c 100%);
  color: white;
  padding: 0px 16px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 400;
}

/* Exception Deadlines Compact Design */
.exceptions-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.exception-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%);
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  padding: 6px 8px 6px 12px;
  font-size: 13px;
  transition: all 0.2s ease;
}

.exception-chip:hover {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border-color: rgb(92, 184, 96);
}

.exception-content {
  display: flex;
  align-items: center;
}

.exception-date {
  font-weight: 500;
  color: #424242;
}

.exception-divider {
  margin: 0 6px;
  color: #bdbdbd;
}

.exception-time {
  color: #616161;
}

.exception-actions {
  display: flex;
  align-items: center;
  margin-left: 4px;
}

.exception-actions .action-btn {
  width: 22px;
  height: 22px;
  margin-left: 2px;
}

.exception-actions .action-btn:hover {
  background: rgba(0, 0, 0, 0.08);
}

.no-exceptions {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #fafafa;
  border: 1px dashed #e0e0e0;
  border-radius: 8px;
}

/* Copy from link styling */
.copy-from-link {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  color: #4caf50;
  cursor: pointer;
  margin-top: 4px;
  transition: color 0.2s ease;
}

.copy-from-link:hover {
  color: #388e3c;
  text-decoration: underline;
}

.copy-from-link .v-icon {
  color: #4caf50 !important;
  font-size: 12px !important;
}

.copy-from-link:hover .v-icon {
  color: #388e3c !important;
}

/* Grades List Compact Design */
.grades-list {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  max-height: 300px;
  overflow-y: auto;
}

.grade-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 12px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.grade-row:last-child {
  border-bottom: none;
}

.grade-row:hover {
  background-color: #f5f5f5;
}

.grade-select {
  display: flex;
  align-items: center;
  margin-right: 8px;
}

.grade-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.grade-name {
  font-weight: 600;
  color: #424242;
  min-width: 80px;
}

.grade-branch {
  color: #757575;
  font-size: 13px;
  margin-left: 16px;
}

.grade-actions {
  display: flex;
  align-items: center;
  opacity: 0.6;
  transition: opacity 0.15s ease;
}

.grade-row:hover .grade-actions {
  opacity: 1;
}

.no-grades {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #fafafa;
}

.compact-search {
  border-radius: 6px;
}

/* Deadline Time Picker */
.deadline-time-picker {
  width: 100%;
}

/* Unsaved Warning */
.unsaved-warning {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #f57c00;
  padding: 8px 12px;
  background: #fff3e0;
  border: 1px solid #ffcc80;
  border-radius: 4px;
}

/* Route Selection Styling */
.route-selection-wrapper {
  width: 100%;
  overflow: hidden;
}

.route-selection-main {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  line-height: 1.4;
}

.route-selection-driver {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #666;
  margin-top: 2px;
  line-height: 1.2;
}

.route-detail-chip {
  display: inline-flex;
  align-items: center;
  background: rgb(92, 184, 96); /* Default fallback color */
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  margin-left: 4px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

/* Add spacing between route list items - only for bus route selections */
.bus-change-dialog .route-list-item {
  padding-top: 12px;
  padding-bottom: 12px;
  padding-left: 8px;
  transition: all 0.2s ease;
}

.bus-change-dialog .route-list-item:hover {
  background-color: rgba(92, 184, 96, 0.05);
}

/* Account Search Styles */
.search-toggle {
  height: 32px !important;
  border-radius: 6px;
}

.search-toggle .v-btn {
  font-size: 13px;
  padding: 6px 12px !important;
  min-width: 70px;
}

.search-toggle.v-btn-toggle .v-btn.v-btn--active {
  background-color: rgb(92, 184, 96) !important;
  color: white !important;
}

.account-list-container {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
}

.account-list {
  background: transparent;
}

.account-item {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #eeeeee;
}

.account-item:hover {
  background-color: #e8f5e9 !important;
}

.account-item:last-child {
  border-bottom: none;
}

.selected-account-card {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border: 2px solid #2196f3 !important;
  border-radius: 12px;
  padding: 8px;
}

/* Report Dialog Styles */
.report-table-container {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: white;
}

.report-table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 16px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  min-height: 36px;
}

.report-table-scroll {
  max-height: 400px;
  overflow-y: auto;
}

.report-table-pagination {
  padding: 12px 16px;
  border-top: 1px solid #e0e0e0;
  background: #fafafa;
}

.report-data-table {
  border-radius: 0 !important;
}

.bus-change-main-info {
  font-weight: 500;
  color: #212121;
}

.bus-change-sub-info {
  font-size: 11px;
  color: #757575;
  margin-top: 3px;
  line-height: 1.5;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.bus-change-sub-info span {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.bus-change-sub-info .v-icon {
  vertical-align: middle;
  margin-right: 2px;
}

.report-filters-row {
  flex-wrap: nowrap !important;
}

.report-filters-row > .flex {
  flex-shrink: 1;
  min-width: 0;
}

/* Selected Grades List in Bulk Exception Dialog */
.selected-grades-list {
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
  padding: 4px;
}

.selected-grade-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: white;
  border-radius: 6px;
  margin-bottom: 4px;
  transition: background-color 0.2s ease;
}

.selected-grade-item:last-child {
  margin-bottom: 0;
}

.selected-grade-item:hover {
  background-color: #e8f5e9;
}

.selected-grade-item .grade-text {
  font-size: 14px;
  line-height: 1.4;
}
</style>
